<template>
  <div class="sync-page-container">
    <!-- Hero Banner -->
    <div class="sync-hero-banner staff-ui-header mb-4 d-flex flex-column justify-content-center">
      <div class="d-flex align-items-center">
        <span class="sync-header-icon mr-3 d-inline-flex align-items-center text-white">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
          </svg>
        </span>
        <div class="sync-header-text">
          <h4 class="card-title mb-0 text-white font-weight-bold">
            {{ $t('requesterOrgSync.title') }}
          </h4>
          <p class="sync-header-sub mb-0 mt-1">
            {{ $t('requesterOrgSync.description') }} &bull; {{ $t('requesterOrgSync.createOnlyNotice') }}
          </p>
        </div>
      </div>
    </div>

    <!-- PROMINENT STATUS BANNER (GREEN / RED / NEUTRAL) -->
    <div
      v-if="statusBannerType === 'GREEN'"
      class="sync-prominent-status sync-prominent-status--green p-3 mb-4 rounded-lg d-flex align-items-center shadow-sm"
      id="statusBannerGreen"
    >
      <div class="sync-banner-icon-box mr-3 bg-success text-white rounded-circle p-2 d-flex align-items-center justify-content-center">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
          <polyline points="22 4 12 14.01 9 11.01"></polyline>
        </svg>
      </div>
      <div>
        <h5 class="mb-1 font-weight-bold text-success">{{ $t('requesterOrgSync.statusGreenTitle') }}</h5>
        <p class="mb-0 text-muted small">{{ $t('requesterOrgSync.statusGreenDesc') }} &bull; {{ $t('requesterOrgSync.lastCheck') }}: {{ formatDate(status.lastCheckAt) }}</p>
      </div>
    </div>

    <div
      v-else-if="statusBannerType === 'RED'"
      class="sync-prominent-status sync-prominent-status--red p-3 mb-4 rounded-lg d-flex align-items-center shadow-sm"
      id="statusBannerRed"
    >
      <div class="sync-banner-icon-box mr-3 bg-danger text-white rounded-circle p-2 d-flex align-items-center justify-content-center">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
      </div>
      <div>
        <h5 class="mb-1 font-weight-bold text-danger">{{ $t('requesterOrgSync.statusRedTitle') }}</h5>
        <p class="mb-0 text-muted small">
          {{ statusRedDetailsText }}
          &bull; {{ $t('requesterOrgSync.lastCheck') }}: {{ formatDate(status.lastCheckAt) }}
        </p>
      </div>
    </div>

    <div
      v-else
      class="sync-prominent-status sync-prominent-status--neutral p-3 mb-4 rounded-lg d-flex align-items-center shadow-sm"
      id="statusBannerNeutral"
    >
      <div class="sync-banner-icon-box mr-3 bg-secondary text-white rounded-circle p-2 d-flex align-items-center justify-content-center">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="16" x2="12" y2="12"></line>
          <line x1="12" y1="8" x2="12.01" y2="8"></line>
        </svg>
      </div>
      <div>
        <h5 class="mb-1 font-weight-bold text-dark">{{ $t('requesterOrgSync.statusWarningTitle') }}</h5>
        <p class="mb-0 text-muted small">
          {{ $t('requesterOrgSync.statusWarningDesc') }}
          <span v-if="status.lastCheckAt"> &bull; {{ $t('requesterOrgSync.lastCheck') }}: {{ formatDate(status.lastCheckAt) }}</span>
        </p>
      </div>
    </div>

    <!-- SECTION 1: LATEST VERIFICATION STATUS -->
    <CCard class="sync-card mb-4 border-0 shadow-sm">
      <CCardHeader class="sync-card-header bg-white d-flex justify-content-between align-items-center flex-wrap">
        <div class="d-flex align-items-center">
          <span class="sync-card-badge-icon mr-2 text-primary">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
          </span>
          <span class="sync-card-title font-weight-bold">{{ $t('requesterOrgSync.statusPanel') }}</span>
        </div>
        <div class="sync-status-notice badge badge-light text-muted font-weight-normal py-1 px-2 border mt-1 mt-md-0">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="mr-1">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="16" x2="12" y2="12"></line>
            <line x1="12" y1="8" x2="12.01" y2="8"></line>
          </svg>
          {{ $t('requesterOrgSync.statusPanelNotice') }}
        </div>
      </CCardHeader>

      <CCardBody class="sync-card-body pt-3">
        <!-- Top Status Key-Value Grid -->
        <CRow>
          <!-- Left Column -->
          <CCol md="6">
            <div class="sync-status-row d-flex justify-content-between align-items-center py-2 border-bottom">
              <span class="sync-status-label text-muted">{{ $t('requesterOrgSync.blendataStatus') }}</span>
              <span class="sync-status-value">
                <span :class="statusBadgeClass">{{ status.lastCheckStatus || '-' }}</span>
              </span>
            </div>

            <div class="sync-status-row d-flex justify-content-between align-items-center py-2 border-bottom">
              <span class="sync-status-label text-muted">{{ $t('requesterOrgSync.masterDataStatus') }}</span>
              <span class="sync-status-value">
                <span class="badge badge-secondary">{{ status.masterDataStatus || 'UNKNOWN' }}</span>
              </span>
            </div>

            <div class="sync-status-row d-flex justify-content-between align-items-center py-2 border-bottom">
              <span class="sync-status-label text-muted">{{ $t('requesterOrgSync.lastCheck') }}</span>
              <span class="sync-status-value font-weight-medium text-dark small">{{ formatDate(status.lastCheckAt) }}</span>
            </div>

            <div class="sync-status-row d-flex justify-content-between align-items-center py-2 border-bottom">
              <span class="sync-status-label text-muted">{{ $t('requesterOrgSync.lastSuccessfulSync') }}</span>
              <span class="sync-status-value font-weight-medium text-dark small">{{ formatDate(status.lastSuccessfulSyncAt) }}</span>
            </div>
          </CCol>

          <!-- Right Column -->
          <CCol md="6">
            <div class="sync-status-row d-flex justify-content-between align-items-center py-2 border-bottom">
              <span class="sync-status-label text-muted">{{ $t('requesterOrgSync.nextCheck') }}</span>
              <span class="sync-status-value font-weight-medium text-dark small">{{ formatDate(status.nextScheduledCheckAt) }}</span>
            </div>

            <div class="sync-status-row d-flex justify-content-between align-items-center py-2 border-bottom">
              <span class="sync-status-label text-muted">{{ isEn ? 'Next Scheduled Sync' : 'เวลาซิงค์ถัดไป' }}</span>
              <span class="sync-status-value font-weight-medium text-dark small">{{ formatDate(status.nextScheduledSyncAt) }}</span>
            </div>

            <div class="sync-status-row d-flex justify-content-between align-items-center py-2 border-bottom">
              <span class="sync-status-label text-muted">{{ $t('requesterOrgSync.reviewRequired') }}</span>
              <span class="sync-status-value font-weight-bold" :class="status.reviewRequired ? 'text-danger' : 'text-success'">
                {{ status.reviewRequired ? 'Yes' : 'No' }}
              </span>
            </div>

            <div class="sync-status-row d-flex justify-content-between align-items-center py-2 border-bottom">
              <span class="sync-status-label text-muted">{{ isEn ? 'Saved Auto Sync Setting' : 'การตั้งค่า Auto Sync ที่บันทึกไว้' }}</span>
              <span class="sync-status-value">
                <span
                  id="savedAutoSyncSettingBadge"
                  :class="savedAutoSyncEnabled ? 'badge badge-success' : 'badge badge-secondary'"
                >
                  {{ savedAutoSyncEnabled ? (isEn ? 'Enabled' : 'เปิดใช้งาน') : (isEn ? 'Disabled' : 'ปิดใช้งาน') }}
                </span>
              </span>
            </div>

            <div class="sync-status-row d-flex justify-content-between align-items-center py-2 border-bottom">
              <span class="sync-status-label text-muted">{{ isEn ? 'Auto Sync Worker Status' : 'สถานะ Worker ซิงค์อัตโนมัติ (Auto Sync)' }}</span>
              <span class="sync-status-value">
                <span
                  id="automaticApplyRuntimeBadge"
                  :class="(status.autoSyncSchedulerActive || status.automaticApplyActive) ? 'badge badge-success' : 'badge badge-secondary'"
                >
                  {{ (status.autoSyncSchedulerActive || status.automaticApplyActive) ? $t('requesterOrgSync.autoApplyActive') : $t('requesterOrgSync.autoApplyInactive') }}
                </span>
              </span>
            </div>
          </CCol>
        </CRow>

        <!-- Warnings & Error displays if present -->
        <div class="mt-3" v-if="status.schedulerWarning">
          <div class="alert alert-warning py-2 px-3 mb-2 small rounded-lg">
            <strong>{{ $t('requesterOrgSync.schedulerWarning') }}:</strong>
            <span class="font-monospace ml-1">{{ status.schedulerWarning }}</span>
          </div>
        </div>

        <div class="mt-2" v-if="status.lastSafeErrorCode">
          <div class="alert alert-danger py-2 px-3 mb-2 small rounded-lg">
            <strong>{{ $t('requesterOrgSync.lastSafeError') }}:</strong>
            <span class="text-danger font-monospace small ml-1">{{ status.lastSafeErrorCode }}</span>
          </div>
        </div>

        <!-- Decision Info Row if present -->
        <CRow class="mt-3 pt-2 border-top" v-if="status.autoSyncDecision">
          <CCol md="6">
            <div class="sync-status-row d-flex justify-content-between align-items-center py-2">
              <span class="sync-status-label text-muted">{{ $t('requesterOrgSync.autoSyncDecision') }}</span>
              <span class="sync-status-value">
                <span id="decisionBadge" :class="decisionBadgeClass">
                  {{ decisionText }}
                </span>
              </span>
            </div>
          </CCol>
          <CCol md="6" v-if="status.autoSyncDecisionReasonCode">
            <div class="sync-status-row d-flex justify-content-between align-items-center py-2">
              <span class="sync-status-label text-muted">{{ $t('requesterOrgSync.decisionReason') }}</span>
              <span id="decisionReasonCode" class="sync-status-value font-monospace text-muted small">
                {{ status.autoSyncDecisionReasonCode }}
              </span>
            </div>
          </CCol>
        </CRow>

        <!-- Comparison Fingerprint if present -->
        <div class="text-right text-muted small mt-1" v-if="status.comparisonFingerprint">
          {{ $t('requesterOrgSync.decisionFingerprint') }}:
          <span id="decisionFingerprintText" class="font-monospace text-dark">{{ short(status.comparisonFingerprint) }}</span>
        </div>

        <!-- Decision alerts -->
        <div class="mt-3" v-if="status.autoSyncDecision === 'SAFE_CREATE_NEW'">
          <div class="alert alert-info py-2 px-3 mb-0 small rounded-lg" id="safeCreateNotice">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="mr-1">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="16" x2="12" y2="12"></line>
              <line x1="12" y1="8" x2="12.01" y2="8"></line>
            </svg>
            {{ $t('requesterOrgSync.safeCreateNotice') }}
          </div>
        </div>

        <div class="mt-3" v-if="status.autoSyncDecision === 'REVIEW_REQUIRED'">
          <div class="alert alert-warning py-2 px-3 mb-0 small rounded-lg" id="reviewRequiredNotice">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="mr-1">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
              <line x1="12" y1="9" x2="12" y2="13"></line>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
            {{ $t('requesterOrgSync.reviewRequiredNotice') }}
          </div>
        </div>

        <div class="mt-3" v-if="status.autoSyncDecision === 'BLOCKED'">
          <div class="alert alert-danger py-2 px-3 mb-0 small rounded-lg" id="blockedNotice">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="mr-1">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="15" y1="9" x2="9" y2="15"></line>
              <line x1="9" y1="9" x2="15" y2="15"></line>
            </svg>
            {{ $t('requesterOrgSync.blockedNotice') }}
          </div>
        </div>
      </CCardBody>
    </CCard>

    <!-- SECTION 2: ONE-SHOT SYNC NOW & WORKFLOW -->
    <CCard class="sync-card mb-4 border-0 shadow-sm">
      <CCardHeader class="sync-card-header bg-white d-flex justify-content-between align-items-center">
        <div class="d-flex align-items-center">
          <span class="sync-card-badge-icon mr-2 text-primary">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
            </svg>
          </span>
          <span class="sync-card-title font-weight-bold">{{ isEn ? 'Sync Requester Affiliation Data Now' : 'ซิงค์ข้อมูลสังกัดผู้แจ้งซ่อมตอนนี้' }}</span>
        </div>
      </CCardHeader>

      <CCardBody class="sync-card-body">
        <!-- Prominent One-shot Sync Action Box -->
        <div class="sync-primary-action-box p-3 bg-light rounded-lg d-flex justify-content-between align-items-center flex-wrap mb-3 border">
          <div class="mb-2 mb-md-0 pr-md-3">
            <h6 class="font-weight-bold mb-1 text-dark">
              {{ $t('requesterOrgSync.syncNowBtn') }}
            </h6>
            <p class="text-muted small mb-0">
              {{ $t('requesterOrgSync.syncNowDesc') }}
            </p>
          </div>
          <div class="d-flex align-items-center flex-wrap gap-2">
            <!-- ONE-SHOT SYNC BUTTON -->
            <CButton
              id="syncNowBtn"
              color="primary"
              class="sync-btn sync-btn--primary px-4 py-2 font-weight-bold mb-1"
              :disabled="busy"
              @click="syncNow"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" class="mr-1">
                <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
              </svg>
              {{ syncingNow || (busy && state === 'SYNC_NOW_LOADING') ? (isEn ? 'Syncing...' : 'กำลังซิงค์ข้อมูล...') : $t('requesterOrgSync.syncNowBtn') }}
            </CButton>

            </div>
        </div>

        <!-- WORKSPACE: UPDATE ORGANIZATION & CHANGE HISTORY TABS -->
        <div id="approvalWorkspace" class="sync-approval-workspace mt-4">
          <div class="border rounded-lg p-3 bg-white shadow-sm mb-4">
            <!-- Header & Summary -->
            <div class="d-flex justify-content-between align-items-center flex-wrap pb-3 mb-3 border-bottom">
              <div>
                <h5 class="font-weight-bold text-dark mb-1">
                  {{ isEn ? 'Organization Updates & History' : 'การอัปเดตสังกัดและประวัติการทำงาน' }}
                  <span v-if="activeTab === 'UPDATE_ORG'" class="badge badge-info ml-2">{{ recentUpdates.length }} {{ isEn ? 'Items' : 'รายการ (3 วัน)' }}</span>
                </h5>
                <p class="text-muted small mb-0">
                  {{ isEn
                    ? 'Auto Sync updates affiliations directly into the database and retains them here for 3 days with a revert option. Manual Sync allows previewing changes before saving.'
                    : 'Auto Sync จะทำการเขียนข้อมูลลงฐานข้อมูลโดยอัตโนมัติ และแสดงรายการในหน้านี้ 3 วันเพื่อเปิดให้ยกเลิกได้ ส่วนการ Sync data now จะมีหน้าต่างยืนยันการเปลี่ยนแปลงก่อนบันทึก' }}
                </p>
              </div>
            </div>

            <!-- Tabs Navigation: Only Update Organization & History -->
            <div class="sync-approval-tabs d-flex flex-wrap gap-2 mb-3" role="tablist">
              <button
                type="button"
                id="tabUpdateOrg"
                class="btn btn-sm mr-2 mb-2 d-flex align-items-center"
                :class="activeTab === 'UPDATE_ORG' ? 'btn-primary font-weight-bold' : 'btn-outline-primary'"
                @click="switchTab('UPDATE_ORG')"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="mr-1">
                  <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
                </svg>
                <span>Update Organization</span>
                <span class="badge badge-light ml-2 text-dark">{{ recentUpdates.length }}</span>
              </button>

              <button
                type="button"
                id="tabChangeHistory"
                class="btn btn-sm mb-2 d-flex align-items-center"
                :class="activeTab === 'CHANGE_HISTORY' ? 'btn-primary font-weight-bold' : 'btn-outline-primary'"
                @click="switchTab('CHANGE_HISTORY')"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="mr-1">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 14 14"></polyline>
                </svg>
                <span>History</span>
              </button>
            </div>

            <!-- TAB 1: UPDATE ORGANIZATION (RECENT AUTO UPDATES) -->
            <div v-show="activeTab === 'UPDATE_ORG'" id="updateOrgSection" class="sync-tab-content">
              <!-- Notice Banner -->
              <div class="alert alert-info py-2 px-3 mb-3 small rounded-lg d-flex align-items-start">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="mt-1 mr-2 flex-shrink-0 text-info">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="16" x2="12" y2="12"></line>
                  <line x1="12" y1="8" x2="12.01" y2="8"></line>
                </svg>
                <div>
                  <div class="font-weight-bold">
                    {{ isEn ? 'Automatic Affiliation Updates (Auto Sync)' : 'รายการที่ Auto Sync อัปเดตข้อมูลอัตโนมัติ' }}
                  </div>
                  <div class="text-muted">
                    {{ isEn 
                      ? 'Recent changes applied automatically by background Auto Sync. Items remain visible for 3 days from the update time. Administrators can revert a change to restore the previous affiliation.' 
                      : 'รายการที่ระบบ Auto Sync อัปเดตเข้าสู่ฐานข้อมูลอัตโนมัติ รายการจะคงอยู่เป็นเวลา 3 วันหลังจากเกิดการเปลี่ยนแปลง ผู้ดูแลระบบสามารถกด \"ยกเลิก\" เพื่อเปลี่ยนกลับเป็นสังกัดก่อนหน้านี้ได้' 
                    }}
                  </div>
                </div>
              </div>

              <!-- Action Bar / Quick Refresh -->
              <div class="d-flex justify-content-between align-items-center mb-3">
                <span class="small text-muted">
                  {{ isEn ? `Found ${recentUpdates.length} recent update(s)` : `พบ ${recentUpdates.length} รายการที่อัปเดตล่าสุด` }}
                </span>
                <button
                  type="button"
                  id="refreshRecentUpdatesBtn"
                  class="btn btn-sm btn-outline-secondary d-flex align-items-center"
                  :disabled="loadingRecentUpdates"
                  @click="loadRecentUpdates"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="mr-1">
                    <polyline points="23 4 23 10 17 10"></polyline>
                    <polyline points="1 20 1 14 7 14"></polyline>
                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
                  </svg>
                  <span>{{ isEn ? 'Refresh' : 'รีเฟรช' }}</span>
                </button>
              </div>

              <!-- Loading State -->
              <div v-if="loadingRecentUpdates" class="text-center py-5 bg-light rounded border">
                <div class="spinner-border spinner-border-sm text-primary mr-2" role="status"></div>
                <span class="text-muted small">{{ isEn ? 'Loading recent updates...' : 'กำลังโหลดข้อมูลการอัปเดตล่าสุด...' }}</span>
              </div>

              <!-- Table with Data -->
              <div v-else-if="recentUpdates && recentUpdates.length > 0">
                <div class="table-responsive border rounded bg-white shadow-sm">
                  <table class="table table-sm table-hover mb-0">
                    <thead class="thead-light">
                      <tr>
                        <th style="min-width: 150px;">{{ isEn ? 'Updated At' : 'วันเวลาที่อัปเดต' }}</th>
                        <th style="min-width: 120px;">{{ isEn ? 'Code' : 'รหัสสังกัด' }}</th>
                        <th style="min-width: 250px;">{{ isEn ? 'Affiliation Details' : 'รายละเอียดการเปลี่ยนแปลงสังกัด' }}</th>
                        <th style="min-width: 120px;" class="text-center">{{ isEn ? 'Change Type' : 'ประเภทการเปลี่ยนแปลง' }}</th>
                        <th style="min-width: 150px;">{{ isEn ? 'Disappears In' : 'เวลาคงเหลือก่อนหายไป' }}</th>
                        <th style="min-width: 100px;" class="text-center">{{ isEn ? 'Action' : 'จัดการ' }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="item in recentUpdates" :key="item.id">
                        <td class="align-middle small">
                          <div class="font-weight-medium text-dark">{{ item.appliedAtThai || formatDate(item.appliedAt) }}</div>
                          <small class="text-muted d-block">
                            <span class="badge badge-light border text-info">Auto Sync</span>
                          </small>
                        </td>
                        <td class="align-middle">
                          <code>{{ item.code }}</code>
                        </td>
                        <td class="align-middle small">
                          <div v-if="item.previousName && item.previousName !== '-'" class="text-muted">
                            <del>{{ item.previousName }}</del>
                          </div>
                          <div class="font-weight-bold text-success">
                            → {{ item.name }}
                          </div>
                        </td>
                        <td class="align-middle text-center">
                          <span v-if="item.changeType === 'CREATE_NEW'" class="badge badge-info py-1 px-2">
                            {{ isEn ? 'New Unit' : 'สร้างใหม่' }}
                          </span>
                          <span v-else class="badge badge-primary py-1 px-2">
                            {{ isEn ? 'Update Name' : 'อัปเดตชื่อ' }}
                          </span>
                        </td>
                        <td class="align-middle small">
                          <div class="d-flex align-items-center text-secondary">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="mr-1 text-muted">
                              <circle cx="12" cy="12" r="10"></circle>
                              <polyline points="12 6 12 12 14 14"></polyline>
                            </svg>
                            <span>{{ item.remainingTimeFormatted }}</span>
                          </div>
                          <small class="text-muted d-block font-italic">
                            {{ isEn ? 'Expires' : 'หมดอายุ' }}: {{ item.expiresAtThai || formatDate(item.expiresAt) }}
                          </small>
                        </td>
                        <td class="align-middle text-center">
                          <button
                            type="button"
                            class="btn btn-xs btn-outline-danger d-inline-flex align-items-center"
                            :disabled="revertingLogId === item.id"
                            @click="confirmRevert(item)"
                            :title="isEn ? 'Revert to previous affiliation' : 'เปลี่ยนกลับเป็นสังกัดก่อนหน้านี้'"
                          >
                            <span v-if="revertingLogId === item.id" class="spinner-border spinner-border-sm mr-1" role="status"></span>
                            <svg v-else width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="mr-1">
                              <polyline points="1 4 1 10 7 10"></polyline>
                              <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"></path>
                            </svg>
                            <span>{{ isEn ? 'Revert' : 'ยกเลิก' }}</span>
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Empty State -->
              <div v-else class="text-center py-5 bg-light rounded-lg border">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#64748b" stroke-width="1.8" class="mb-2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
                <h6 class="font-weight-bold text-dark mb-1">
                  {{ isEn ? 'No Recent Automatic Updates' : 'ไม่มีรายการอัปเดตอัตโนมัติล่าสุด' }}
                </h6>
                <p class="text-muted small mb-0">
                  {{ isEn 
                    ? 'All organization affiliations are up to date. Updates made by Auto Sync will appear here for 3 days.' 
                    : 'ข้อมูลสังกัดทั้งหมดเป็นปัจจุบันแล้ว เมื่อ Auto Sync ทำการเปลี่ยนแปลง รายการจะแสดงที่นี่เป็นเวลา 3 วัน' 
                  }}
                </p>
              </div>
            </div>

            <!-- TAB 4: CHANGE HISTORY -->
            <div v-show="activeTab === 'CHANGE_HISTORY'" id="changeHistorySection" class="sync-tab-content">
              <!-- Search & Filters -->
              <div class="p-3 bg-light rounded-lg border mb-3">
                <div class="row align-items-end">
                  <div class="col-12 col-md-4 mb-2 mb-md-0">
                    <label class="small text-muted font-weight-bold mb-1">{{ isEn ? 'Search' : 'ค้นหา' }}</label>
                    <input
                      type="text"
                      id="historySearchInput"
                      class="form-control form-control-sm"
                      :placeholder="$t('requesterOrgSync.historySearchPlaceholder')"
                      v-model="historySearch"
                      @keyup.enter="loadHistory(1)"
                    />
                  </div>
                  <div class="col-12 col-sm-6 col-md-3 mb-2 mb-md-0">
                    <label class="small text-muted font-weight-bold mb-1">{{ $t('requesterOrgSync.historyColAction') }}</label>
                    <select id="historyActionSelect" class="form-control form-control-sm" v-model="historyActionType" @change="loadHistory(1)">
                      <option value="">{{ $t('requesterOrgSync.historyActionAll') }}</option>
                      <option value="AUTO_SYNC">{{ isEn ? 'Auto Sync (Automatic)' : 'Auto Sync (อัปเดตอัตโนมัติ)' }}</option>
                      <option value="MANUAL_SYNC">{{ isEn ? 'Manual Sync (Admin Applied)' : 'Manual Sync (ยืนยันเอง)' }}</option>
                      <option value="REVERT">{{ isEn ? 'Revert (Reverted Changes)' : 'Revert (ยกเลิกการแก้ไข)' }}</option>
                    </select>
                  </div>
                  <div class="col-6 col-sm-3 col-md-2 mb-2 mb-md-0">
                    <label class="small text-muted font-weight-bold mb-1">{{ $t('requesterOrgSync.historyDateFrom') }}</label>
                    <input type="date" id="historyStartDate" class="form-control form-control-sm" v-model="historyStartDate" @change="loadHistory(1)" />
                  </div>
                  <div class="col-6 col-sm-3 col-md-2 mb-2 mb-md-0">
                    <label class="small text-muted font-weight-bold mb-1">{{ $t('requesterOrgSync.historyDateTo') }}</label>
                    <input type="date" id="historyEndDate" class="form-control form-control-sm" v-model="historyEndDate" @change="loadHistory(1)" />
                  </div>
                  <div class="col-12 col-md-1 d-flex justify-content-end gap-1">
                    <button type="button" id="historyResetBtn" class="btn btn-sm btn-outline-secondary w-100" @click="resetHistoryFilters">
                      {{ isEn ? 'Reset' : 'ล้าง' }}
                    </button>
                  </div>
                </div>
              </div>

              <!-- Loading Indicator -->
              <div v-if="historyLoading" class="text-center py-4 bg-light rounded border">
                <div class="spinner-border spinner-border-sm text-primary mr-2" role="status"></div>
                <span class="text-muted small">{{ isEn ? 'Loading change history...' : 'กำลังโหลดประวัติการแก้ไข...' }}</span>
              </div>

              <!-- History Table -->
              <div v-else-if="historyItems && historyItems.length > 0">
                <div class="table-responsive border rounded">
                  <table class="table table-sm table-hover mb-0">
                    <thead class="thead-light">
                      <tr>
                        <th style="min-width: 140px;">{{ $t('requesterOrgSync.historyColTime') }}</th>
                        <th style="min-width: 120px;">{{ $t('requesterOrgSync.historyColOperator') }}</th>
                        <th style="min-width: 160px;">{{ $t('requesterOrgSync.historyColOrg') }}</th>
                        <th style="min-width: 130px;">{{ $t('requesterOrgSync.historyColAction') }}</th>
                        <th style="min-width: 200px;">{{ $t('requesterOrgSync.historyColChanges') }}</th>
                        <th style="min-width: 130px;" class="text-center">{{ $t('requesterOrgSync.historyColResult') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="item in historyItems" :key="item.id || item.itemKey">
                        <td class="align-middle small">
                          <div class="font-weight-medium text-dark">{{ item.timestampThai || item.formattedDateThai || formatDate(item.timestamp || item.createdAt) }}</div>
                          <span v-if="item.provenance" class="badge badge-light border text-muted mt-1 small">
                            {{ getProvenanceLabel(item.provenance) }}
                          </span>
                        </td>
                        <td class="align-middle small">
                          <div class="font-weight-bold text-dark">{{ item.operator ? (item.operator.name || item.operator.email || item.operator.userId || item.operator.id) : (isEn ? 'Unknown' : 'ไม่ทราบ') }}</div>
                          <small v-if="item.operator && item.operator.role" class="text-muted text-uppercase">({{ item.operator.role }})</small>
                        </td>
                        <td class="align-middle">
                          <code>{{ item.organizationCode || item.code || '-' }}</code>
                          <div class="font-weight-bold text-dark small mt-1">{{ item.organizationName || item.name || '-' }}</div>
                        </td>
                        <td class="align-middle">
                          <span class="badge" :class="getActionBadgeClass(item.actionType || item.actionCategory)">
                            {{ getActionTypeLabel(item.actionType || item.actionCategory) }}
                          </span>
                        </td>
                        <td class="align-middle small">
                          <div v-if="getBeforeValue(item) && getBeforeValue(item) !== '-'" class="text-muted">
                            <del>{{ getBeforeValue(item) }}</del>
                          </div>
                          <div v-if="getAfterValue(item) && getAfterValue(item) !== '-'" class="font-weight-bold text-success">
                            → {{ getAfterValue(item) }}
                          </div>
                          <div v-if="(!getBeforeValue(item) || getBeforeValue(item) === '-') && (!getAfterValue(item) || getAfterValue(item) === '-')" class="text-muted font-italic">
                            {{ isEn ? 'Unknown / No Detail' : 'ไม่ทราบ / ไม่มีรายละเอียด' }}
                          </div>
                          <!-- Legacy Alias details if changed -->
                          <div v-if="item.details && item.details.aliasAdded" class="badge badge-light border text-primary mt-1">
                            {{ isEn ? 'Alias Added:' : 'เพิ่ม Alias:' }} {{ item.details.aliasAdded }}
                          </div>
                          <div v-else-if="item.details && item.details.aliases && item.details.aliases.length > 0" class="text-muted small mt-1">
                            Aliases: {{ item.details.aliases.join(', ') }}
                          </div>
                        </td>
                        <td class="align-middle text-center">
                          <div v-if="item.resultStatus === 'SUCCESS' || (item.status === 'SUCCESS' && item.warning !== 'AUDIT_RECORD_MISSING' && item.resultStatus !== 'AUDIT_RECORD_MISSING')">
                            <span class="badge badge-success py-1 px-2 font-weight-normal">{{ isEn ? 'Success' : 'สำเร็จ' }}</span>
                          </div>
                          <div v-else-if="item.resultStatus === 'AUDIT_RECORD_MISSING' || item.warning === 'AUDIT_RECORD_MISSING'">
                            <span class="badge badge-warning text-dark py-1 px-2 font-weight-bold" :title="isEn ? 'Review approved evidence confirmed, individual activity log missing' : 'หลักฐาน Review ยืนยันสำเร็จ แต่ไม่พบบันทึกกิจกรรมรายข้อ'">
                              {{ isEn ? 'Success (Evidence Warning)' : 'สำเร็จ (มีคำเตือนหลักฐาน)' }}
                            </span>
                          </div>
                          <div v-else-if="item.resultStatus === 'PARTIAL' || item.status === 'PARTIAL'">
                            <span class="badge badge-info py-1 px-2 font-weight-normal">{{ isEn ? 'Partial' : 'สำเร็จบางส่วน' }}</span>
                          </div>
                          <div v-else-if="item.resultStatus === 'FAILED' || item.status === 'FAILED'">
                            <span class="badge badge-danger py-1 px-2 font-weight-normal">{{ isEn ? 'Failed' : 'ล้มเหลว' }}</span>
                          </div>
                          <div v-else>
                            <span class="badge badge-light border text-muted py-1 px-2 font-weight-normal">{{ item.resultStatus || item.status || '-' }}</span>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <!-- Server-Side Pagination Bar -->
                <div v-if="historyPagination.totalPages > 1 || historyPagination.total > 0 || historyPagination.totalItems > 0" class="d-flex justify-content-between align-items-center flex-wrap pt-3 mt-2 border-top">
                  <small class="text-muted mb-2 mb-sm-0">
                    {{ isEn
                      ? `Showing page ${historyPagination.page} of ${historyPagination.totalPages} (${(typeof historyPagination.total === 'number' ? historyPagination.total : historyPagination.totalItems) || 0} total records)`
                      : `แสดงหน้า ${historyPagination.page} จาก ${historyPagination.totalPages} (ทั้งหมด ${(typeof historyPagination.total === 'number' ? historyPagination.total : historyPagination.totalItems) || 0} รายการ)`
                    }}
                  </small>
                  <div class="btn-group btn-group-sm">
                    <button
                      type="button"
                      id="historyPrevPageBtn"
                      class="btn btn-outline-primary"
                      :disabled="historyPagination.page <= 1 || historyLoading"
                      @click="loadHistory(historyPagination.page - 1)"
                    >
                      &laquo; {{ isEn ? 'Previous' : 'ก่อนหน้า' }}
                    </button>
                    <button type="button" class="btn btn-outline-primary disabled" disabled>
                      {{ historyPagination.page }} / {{ historyPagination.totalPages }}
                    </button>
                    <button
                      type="button"
                      id="historyNextPageBtn"
                      class="btn btn-outline-primary"
                      :disabled="historyPagination.page >= historyPagination.totalPages || historyLoading"
                      @click="loadHistory(historyPagination.page + 1)"
                    >
                      {{ isEn ? 'Next' : 'ถัดไป' }} &raquo;
                    </button>
                  </div>
                </div>
              </div>

              <!-- Empty State for History -->
              <div v-else class="text-center text-muted py-4 bg-light rounded border">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="mb-2 text-muted">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                <div class="font-weight-medium">{{ $t('requesterOrgSync.historyEmpty') }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- CODE DRIFT RELATIONSHIP MODAL -->
        <div v-if="showCodeDriftModal && activeCodeDriftItem" class="modal-backdrop fade show" style="z-index: 1040;"></div>
        <div v-if="showCodeDriftModal && activeCodeDriftItem" v-dialog-accessibility="{ close: closeCodeDriftModal }" class="modal fade show d-block" role="dialog" aria-modal="true" tabindex="-1" style="z-index: 1050;">
          <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content border-0 shadow">
              <div class="modal-header bg-light">
                <h6 class="modal-title font-weight-bold text-dark">
                  {{ $t('requesterOrgSync.codeDriftDetail') }}
                </h6>
                <button type="button" class="close" :aria-label="$t('common.close')" @click="closeCodeDriftModal">
                  <span>&times;</span>
                </button>
              </div>
              <div class="modal-body">
                <div class="p-3 bg-light rounded mb-3">
                  <div class="small"><strong>Master Data Code:</strong> <code>{{ activeCodeDriftItem.sourceData ? activeCodeDriftItem.sourceData.code : '' }}</code></div>
                  <div class="small"><strong>Master Data Name:</strong> {{ activeCodeDriftItem.sourceData ? activeCodeDriftItem.sourceData.name : '' }}</div>
                  <div v-if="activeCodeDriftItem.targetData" class="small mt-1 text-muted">
                    <strong>Current System Code:</strong> <code>{{ activeCodeDriftItem.targetData.code }}</code> ({{ activeCodeDriftItem.targetData.name }})
                  </div>
                </div>

                <!-- Structured Guidelines for Code Drift -->
                <div class="alert alert-success py-2 px-3 small rounded mb-2">
                  <strong class="d-block mb-1">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="mr-1"><polyline points="20 6 9 17 4 12"></polyline></svg>
                    {{ isEn ? 'What can be saved after Admin mapping:' : 'สิ่งที่สามารถบันทึกได้หลัง Admin จับคู่และยืนยัน:' }}
                  </strong>
                  <ul class="mb-0 pl-3">
                    <li>{{ isEn ? 'Atomically updates code and externalSourceKey to match Master Data.' : 'อัปเดตรหัส code และ externalSourceKey ให้ตรงกับ Master Data แบบ atomic' }}</li>
                    <li>{{ isEn ? 'Preserves existing _id to maintain all User and Ticket references.' : 'คง _id เดิม เพื่อรักษาความสัมพันธ์ของ User และ Ticket ทั้งหมด' }}</li>
                    <li>{{ isEn ? 'Preserves organization name without creating duplicate records.' : 'คงชื่อสังกัดเดิมไว้โดยไม่สร้างเรคคอร์ดซ้ำซ้อน' }}</li>
                  </ul>
                </div>

                <div class="alert alert-danger py-2 px-3 small rounded mb-3">
                  <strong class="d-block mb-1">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="mr-1"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>
                    {{ isEn ? 'What remains blocked / protected:' : 'สิ่งที่ยังคงถูกบล็อกและป้องกันความปลอดภัย:' }}
                  </strong>
                  <ul class="mb-0 pl-3">
                    <li>{{ isEn ? 'CODE_COLLISION: Blocked if the new code already belongs to another unit.' : 'CODE_COLLISION: ไม่อนุญาตหากรหัสใหม่ซ้ำกับสังกัดอื่นในระบบ' }}</li>
                    <li>{{ isEn ? 'Approved Student Faculty Mapping: Units in standard faculty mapping cannot be modified.' : 'หน่วยงานใน Approved Student Faculty Mapping ไม่อนุญาตให้แก้ไขรหัส' }}</li>
                    <li>{{ isEn ? 'Arbitrary guessing / crosswalk across departments is forbidden.' : 'ไม่อนุญาตให้จับคู่ข้ามสังกัดโดยไม่มีหลักฐานยืนยัน' }}</li>
                    <li>{{ isEn ? 'Unmapped items remain blocked and will not be applied.' : 'รายการที่ยังไม่ได้จับคู่จะยังคงถูกระงับและไม่นำไปบันทึก' }}</li>
                  </ul>
                </div>

                <div class="form-group mb-0">
                  <label class="small font-weight-bold text-dark">
                    {{ isEn ? 'Map to Existing OrganizationUnit ID:' : 'เชื่อมโยงกับ OrganizationUnit _id เดิม:' }}
                  </label>
                  <input
                    type="text"
                    class="form-control form-control-sm font-monospace"
                    :value="codeMappings[activeCodeDriftItem.itemKey] || (activeCodeDriftItem.targetData ? activeCodeDriftItem.targetData._id : '')"
                    @input="$set(codeMappings, activeCodeDriftItem.itemKey, $event.target.value)"
                    placeholder="Enter OrganizationUnit _id..."
                  />
                </div>
              </div>
              <div class="modal-footer bg-light py-2">
                <button type="button" class="btn btn-sm btn-secondary" @click="closeCodeDriftModal">
                  {{ $t('common.cancel') }}
                </button>
                <button
                  type="button"
                  class="btn btn-sm btn-primary font-weight-bold"
                  @click="saveCodeDriftMapping(codeMappings[activeCodeDriftItem.itemKey] || (activeCodeDriftItem.targetData ? activeCodeDriftItem.targetData._id : ''))"
                >
                  {{ isEn ? 'Save Relationship' : 'บันทึกความสัมพันธ์' }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Preview Results Display (when preview was invoked) -->
        <div v-if="result" class="sync-preview-result-container mt-3">
          <div class="d-flex justify-content-between align-items-center flex-wrap mb-3">
            <h5 class="mb-0 font-weight-bold text-dark">{{ $t('requesterOrgSync.previewSummary') }}</h5>
            <div v-if="result.upToDate" class="badge badge-success px-3 py-2">
              {{ $t('requesterOrgSync.upToDate') }}
            </div>
          </div>

          <!-- KPI Metric Cards for Preview -->
          <div class="row mb-4">
            <div class="col-6 col-md-3 mb-2">
              <div class="sync-kpi-card p-3 rounded-lg border text-center bg-white shadow-sm">
                <span class="text-muted small d-block mb-1">{{ $t('requesterOrgSync.newData') }}</span>
                <strong class="h4 text-primary font-weight-bold mb-0">{{ result.summary.createNew }}</strong>
              </div>
            </div>
            <div class="col-6 col-md-3 mb-2">
              <div class="sync-kpi-card p-3 rounded-lg border text-center bg-white shadow-sm">
                <span class="text-muted small d-block mb-1">{{ $t('requesterOrgSync.unchanged') }}</span>
                <strong class="h4 text-secondary font-weight-bold mb-0">{{ result.summary.unchanged }}</strong>
              </div>
            </div>
            <div class="col-6 col-md-3 mb-2">
              <div class="sync-kpi-card p-3 rounded-lg border text-center bg-white shadow-sm">
                <span class="text-muted small d-block mb-1">{{ $t('requesterOrgSync.conflicts') }}</span>
                <strong class="h4 text-warning font-weight-bold mb-0">{{ result.summary.conflicts }}</strong>
              </div>
            </div>
            <div class="col-6 col-md-3 mb-2">
              <div class="sync-kpi-card p-3 rounded-lg border text-center bg-white shadow-sm">
                <span class="text-muted small d-block mb-1">{{ $t('requesterOrgSync.reviewRequired') }}</span>
                <strong class="h4 text-danger font-weight-bold mb-0">{{ reviewCount }}</strong>
              </div>
            </div>
          </div>

          <!-- Section: รายการเพิ่มใหม่ (Create-Only Candidates) -->
          <div class="border rounded-lg p-3 mb-4 bg-white shadow-sm">
            <div class="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
              <h6 class="font-weight-bold mb-0 text-dark">
                {{ $t('requesterOrgSync.createCandidates') }}
                <span class="badge badge-primary ml-2">{{ result.summary.createNew }}</span>
              </h6>
            </div>
            <div v-if="result.review && result.review.createCandidates && result.review.createCandidates.length > 0" class="table-responsive">
              <table class="table table-sm table-hover mb-0">
                <thead class="thead-light">
                  <tr>
                    <th class="border-0">Code</th>
                    <th class="border-0">Name</th>
                    <th class="border-0 text-center">Operation</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in result.review.createCandidates" :key="item.externalSourceKey">
                    <td class="font-weight-medium">{{ item.code || item.externalSourceKey }}</td>
                    <td>{{ item.name }}</td>
                    <td class="text-center"><span class="badge badge-info">CREATE_NEW</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="text-muted small py-3 text-center">
              {{ $t('requesterOrgSync.noNewItems') }}
            </div>
          </div>

          <!-- Section: รายการต้องตรวจทาน (Review Required & Blockers) -->
          <div v-if="reviewCount > 0 || !result.applyEligible" class="border border-warning rounded-lg p-3 mb-4 bg-light">
            <div class="d-flex align-items-start mb-2">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-warning mr-2 mt-1 flex-shrink-0">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                <line x1="12" y1="9" x2="12" y2="13"></line>
                <line x1="12" y1="17" x2="12.01" y2="17"></line>
              </svg>
              <div class="w-100">
                <h6 class="font-weight-bold text-dark mb-1">{{ $t('requesterOrgSync.reviewItemsTitle') }}</h6>
                <p class="text-muted small mb-2">{{ $t('requesterOrgSync.reviewRequiredDesc') }}</p>

                <!-- Blocker reason if blocked -->
                <div v-if="!result.applyEligible" class="alert alert-warning py-2 px-3 mb-2 small rounded">
                  <strong>{{ $t('requesterOrgSync.blockerPrefix') }}</strong>
                  <span class="font-monospace ml-1">{{ result.applyBlockCode || (isEn ? 'REVIEW_REQUIRED_BEFORE_APPLY' : 'มีรายการที่ต้องตรวจสอบก่อนดำเนินการ') }}</span>
                </div>

                <!-- Review Items breakdown (from backend summary) -->
                <div class="d-flex flex-wrap text-muted small mt-2">
                  <span class="mr-3 mb-1"><strong>{{ isEn ? 'Conflicts' : 'ข้อมูลขัดแย้ง' }}:</strong> {{ result.summary.conflicts }}</span>
                  <span class="mr-3 mb-1"><strong>{{ isEn ? 'Name Drift' : 'ชื่อเปลี่ยน' }}:</strong> {{ result.summary.nameDrift }}</span>
                  <span class="mr-3 mb-1"><strong>{{ isEn ? 'Quarantined' : 'กักกัน' }}:</strong> {{ result.summary.quarantined }}</span>
                  <span class="mb-1"><strong>{{ isEn ? 'Missing from Source' : 'ไม่พบในต้นทาง' }}:</strong> {{ result.summary.missingFromSource }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Status explanation if up to date -->
          <div v-if="result.upToDate" class="alert alert-success py-2 px-3 mb-4 small rounded">
            {{ $t('requesterOrgSync.upToDate') }} &bull; {{ $t('requesterOrgSync.noApplyNeeded') }}
          </div>

          <!-- Confirm Apply Button & Guidance -->
          <div class="d-flex flex-column align-items-start mb-4">
            <CButton
              id="applyBtn"
              color="success"
              class="sync-btn sync-btn--apply px-4 py-2"
              :disabled="busy || !result.applyEligible"
              @click="confirmApply"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="mr-1">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
              {{ busy && state === 'APPLY_LOADING' ? $t('requesterOrgSync.applying') : $t('requesterOrgSync.addUpdatesBtn', { count: result.summary.createNew }) }}
            </CButton>

            <!-- Disabled guidance text -->
            <small v-if="!result.applyEligible" class="text-muted mt-2">
              <span v-if="result.upToDate">{{ $t('requesterOrgSync.noApplyNeeded') }}</span>
              <span v-else>{{ $t('requesterOrgSync.reviewBlocked') }}</span>
            </small>
          </div>
        </div>
      </CCardBody>
    </CCard>

    <!-- SECTION 3 & 4: SETTINGS ROW (AUTO SYNC & ALWAYS CHECK) -->
    <CRow>
      <!-- SECTION 3: AUTO SYNC SETTINGS -->
      <CCol lg="6" class="mb-4">
        <CCard class="sync-card h-100 border-0 shadow-sm">
          <CCardHeader class="sync-card-header bg-white d-flex justify-content-between align-items-center">
            <div class="d-flex align-items-center">
              <span class="sync-card-badge-icon mr-2 text-primary">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
              </span>
              <div>
                <span class="sync-card-title font-weight-bold d-block">{{ $t('requesterOrgSync.autoSyncTitle') }}</span>
                <small class="text-muted font-weight-normal">{{ $t('requesterOrgSync.autoSyncDesc') }}</small>
              </div>
            </div>
          </CCardHeader>

          <CCardBody class="sync-card-body d-flex flex-column justify-content-between">
            <div class="sync-settings-content">
              <!-- Auto Sync Toggle -->
              <div class="sync-setting-item d-flex justify-content-between align-items-center py-2 border-bottom">
                <div class="sync-setting-info pr-3">
                  <div class="d-flex align-items-center mb-1">
                    <label for="autoSyncToggle" class="sync-toggle-title mb-0 font-weight-bold cursor-pointer">
                      {{ $t('requesterOrgSync.autoSync') }}
                    </label>
                    <span class="badge badge-light-primary ml-2 font-weight-normal small">Auto Apply</span>
                  </div>
                  <span class="sync-setting-desc text-muted small d-block">
                    {{ isEn ? 'Automatically sync organization data from Blendata directly into the database on schedule' : 'ซิงค์ข้อมูลหน่วยงานจาก Blendata และบันทึกลงฐานข้อมูลโดยอัตโนมัติตามกำหนดเวลา' }}
                  </span>
                </div>
                <div class="sync-setting-action">
                  <label class="sync-switch sync-switch--impact" for="autoSyncToggle">
                    <input
                      type="checkbox"
                      class="sync-switch-input"
                      id="autoSyncToggle"
                      v-model="settings.autoSyncEnabled"
                    />
                    <span class="sync-switch-slider"></span>
                  </label>
                </div>
              </div>

              <!-- Auto Sync Safety Notice (Phase 3A) -->
              <div v-if="settings.autoSyncEnabled" class="alert alert-info py-2 px-3 mt-3 mb-3 small rounded-lg" id="autoSyncNotice" role="alert">
                <div class="d-flex align-items-start">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="mt-1 mr-2 flex-shrink-0">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="16" x2="12" y2="12"></line>
                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                  </svg>
                  <span>{{ $t('requesterOrgSync.autoSyncNotice') }}</span>
                </div>
              </div>

              <!-- Schedule Mode Selection (DAY / MONTH / YEAR) -->
              <div class="mt-3">
                <label class="text-muted small font-weight-bold text-uppercase d-block mb-2">
                  {{ isEn ? 'Auto Sync Frequency' : 'รูปแบบความถี่การซิงค์' }}
                </label>
                <div class="btn-group btn-group-sm w-100 mb-3" role="group">
                  <button
                    type="button"
                    class="btn"
                    :class="(settings.autoSyncMode || 'DAY') === 'DAY' ? 'btn-primary font-weight-bold' : 'btn-outline-secondary'"
                    @click="setScheduleMode('DAY')"
                  >
                    {{ $t('requesterOrgSync.modeDay') }}
                  </button>
                  <button
                    type="button"
                    class="btn"
                    :class="settings.autoSyncMode === 'MONTH' ? 'btn-primary font-weight-bold' : 'btn-outline-secondary'"
                    @click="setScheduleMode('MONTH')"
                  >
                    {{ $t('requesterOrgSync.modeMonth') }}
                  </button>
                  <button
                    type="button"
                    class="btn"
                    :class="settings.autoSyncMode === 'YEAR' ? 'btn-primary font-weight-bold' : 'btn-outline-secondary'"
                    @click="setScheduleMode('YEAR')"
                  >
                    {{ $t('requesterOrgSync.modeYear') }}
                  </button>
                </div>

                <!-- Parameters: DAY MODE -->
                <div v-if="(settings.autoSyncMode || 'DAY') === 'DAY'" class="p-3 bg-light rounded-lg border mb-3">
                  <CRow>
                    <CCol sm="6" class="mb-2 mb-sm-0">
                      <label class="small text-muted font-weight-bold d-block mb-1">
                        {{ $t('requesterOrgSync.everyNDays') }}
                      </label>
                      <input
                        type="number"
                        class="form-control form-control-sm"
                        min="1"
                        max="7"
                        v-model.number="settings.autoSyncIntervalDays"
                        @input="onScheduleParamChange"
                      />
                    </CCol>
                    <CCol sm="6">
                      <label class="small text-muted font-weight-bold d-block mb-1">
                        {{ $t('requesterOrgSync.syncTime') }}
                        <span class="badge badge-light border text-muted ml-1">{{ $t('requesterOrgSync.timeThaiLabel') }}</span>
                      </label>
                      <input
                        type="time"
                        class="form-control form-control-sm"
                        v-model="settings.autoSyncTime"
                        @input="onScheduleParamChange"
                      />
                    </CCol>
                  </CRow>
                </div>

                <!-- Parameters: MONTH MODE -->
                <div v-else-if="settings.autoSyncMode === 'MONTH'" class="p-3 bg-light rounded-lg border mb-3">
                  <CRow>
                    <CCol sm="6" class="mb-2 mb-sm-0">
                      <label class="small text-muted font-weight-bold d-block mb-1">
                        {{ $t('requesterOrgSync.dayOfMonth') }}
                      </label>
                      <input
                        type="number"
                        class="form-control form-control-sm"
                        min="1"
                        max="30"
                        v-model.number="settings.autoSyncDayOfMonth"
                        @input="onScheduleParamChange"
                      />
                    </CCol>
                    <CCol sm="6">
                      <label class="small text-muted font-weight-bold d-block mb-1">
                        {{ $t('requesterOrgSync.syncTime') }}
                        <span class="badge badge-light border text-muted ml-1">{{ $t('requesterOrgSync.timeThaiLabel') }}</span>
                      </label>
                      <input
                        type="time"
                        class="form-control form-control-sm"
                        v-model="settings.autoSyncTime"
                        @input="onScheduleParamChange"
                      />
                    </CCol>
                  </CRow>
                  <!-- Clamping notice if day is 29 or 30 -->
                  <div v-if="settings.autoSyncDayOfMonth >= 29" class="text-muted small mt-2">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="mr-1">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="16" x2="12" y2="12"></line>
                      <line x1="12" y1="8" x2="12.01" y2="8"></line>
                    </svg>
                    {{ $t('requesterOrgSync.clampingNote') }}
                  </div>
                </div>

                <!-- Parameters: YEAR MODE -->
                <div v-else-if="settings.autoSyncMode === 'YEAR'" class="p-3 bg-light rounded-lg border mb-3">
                  <CRow>
                    <CCol sm="4" class="mb-2 mb-sm-0">
                      <label class="small text-muted font-weight-bold d-block mb-1">
                        {{ $t('requesterOrgSync.monthOfYear') }}
                      </label>
                      <select
                        class="form-control form-control-sm"
                        v-model.number="settings.autoSyncMonth"
                        @change="onScheduleParamChange"
                      >
                        <option v-for="m in thaiMonths" :key="m.value" :value="m.value">
                          {{ isEn ? m.nameEn : m.nameTh }}
                        </option>
                      </select>
                    </CCol>
                    <CCol sm="4" class="mb-2 mb-sm-0">
                      <label class="small text-muted font-weight-bold d-block mb-1">
                        {{ $t('requesterOrgSync.dayOfMonth') }}
                      </label>
                      <input
                        type="number"
                        class="form-control form-control-sm"
                        min="1"
                        max="30"
                        v-model.number="settings.autoSyncDayOfMonth"
                        @input="onScheduleParamChange"
                      />
                    </CCol>
                    <CCol sm="4">
                      <label class="small text-muted font-weight-bold d-block mb-1">
                        {{ $t('requesterOrgSync.syncTime') }}
                      </label>
                      <input
                        type="time"
                        class="form-control form-control-sm"
                        v-model="settings.autoSyncTime"
                        @input="onScheduleParamChange"
                      />
                    </CCol>
                  </CRow>
                  <div class="text-muted small mt-2">
                    <span class="badge badge-light border text-muted mr-1">{{ $t('requesterOrgSync.timeThaiLabel') }}</span>
                    <span v-if="settings.autoSyncDayOfMonth >= 29">{{ $t('requesterOrgSync.clampingNote') }}</span>
                  </div>
                </div>

                <!-- Live Calculated Preview Box (Before Saving) -->
                <div class="p-3 bg-white rounded-lg border shadow-sm mt-3">
                  <div class="d-flex justify-content-between align-items-center mb-1">
                    <span class="small font-weight-bold text-dark">{{ $t('requesterOrgSync.previewScheduleTitle') }}</span>
                    <span class="badge badge-light text-muted small border">{{ isEn ? 'Unsaved Preview' : 'ตัวอย่างก่อนบันทึก' }}</span>
                  </div>
                  <div v-if="schedulePreview && schedulePreview.data" class="small">
                    <div class="font-weight-bold text-primary">
                      {{ schedulePreview.data.nextDayOfWeek }} ({{ formatDate(schedulePreview.data.nextScheduledSyncAt) }})
                    </div>
                    <div class="text-muted mt-1">{{ schedulePreview.data.explanation }}</div>
                    <div v-if="schedulePreview.data.wasClamped" class="badge badge-warning text-dark mt-1">
                      {{ isEn ? 'Adjusted to month-end' : 'ปรับเป็นวันสิ้นเดือนอัตโนมัติ' }}
                    </div>
                  </div>
                  <div v-else class="text-muted small">
                    {{ isEn ? 'Calculating schedule...' : 'กำลังคำนวณตารางเวลา...' }}
                  </div>
                </div>

                <!-- Currently Active Next Sync (From Backend) -->
                <div class="mt-3 pt-2 border-top d-flex justify-content-between align-items-center small text-muted">
                  <span>{{ $t('requesterOrgSync.nextSyncCalculated') }}</span>
                  <span class="font-weight-bold text-dark">
                    {{ status.nextScheduledSyncAt ? formatDate(status.nextScheduledSyncAt) : $t('requesterOrgSync.autoSyncUnconfigured') }}
                  </span>
                </div>
              </div>
            </div>
          </CCardBody>
        </CCard>
      </CCol>

      <!-- SECTION 4: ALWAYS CHECK SETTINGS -->
      <CCol lg="6" class="mb-4">
        <CCard class="sync-card h-100 border-0 shadow-sm">
          <CCardHeader class="sync-card-header bg-white d-flex justify-content-between align-items-center flex-wrap">
            <div class="d-flex align-items-center">
              <span class="sync-card-badge-icon mr-2 text-secondary">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="12" r="3"></circle>
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                </svg>
              </span>
              <div>
                <span class="sync-card-title font-weight-bold d-block">{{ $t('requesterOrgSync.alwaysCheckTitle') }}</span>
                <small class="text-muted font-weight-normal">{{ isEn ? 'Backup change detection system that runs when Auto Sync is inactive' : 'ระบบตรวจสอบความต่างสำรอง ทำงานอัตโนมัติเมื่อ Auto Sync ไม่ได้เปิด' }}</small>
              </div>
            </div>
            <!-- Status Badge on Card Header -->
            <div class="mt-1 mt-md-0">
              <span
                v-if="settings.autoSyncEnabled"
                class="badge badge-secondary py-1 px-2"
                :title="isEn ? 'Always Check is in standby because Auto Sync is currently active' : 'Always Check อยู่ในสถานะสแตนด์บายเนื่องจาก Auto Sync กำลังเปิดใช้งาน'"
              >
                {{ isEn ? 'Standby (Auto Sync Active)' : 'Standby (รอ Auto Sync ปิด)' }}
              </span>
              <span
                v-else-if="status.schedulerActive"
                id="schedulerStatusBadge"
                class="badge badge-success py-1 px-2"
              >
                {{ isEn ? 'Active (Running)' : 'Active (กำลังทำงาน)' }}
              </span>
              <span
                v-else
                class="badge badge-light border text-muted py-1 px-2"
              >
                {{ isEn ? 'Inactive' : 'ยังไม่ทำงาน' }}
              </span>
            </div>
          </CCardHeader>

          <CCardBody class="sync-card-body d-flex flex-column justify-content-between">
            <div class="sync-settings-content">
              <!-- Notice Banner on Always Check Card -->
              <div
                v-if="settings.autoSyncEnabled"
                class="alert alert-secondary py-2 px-3 mb-3 small rounded-lg border d-flex align-items-center"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="mr-2 text-muted flex-shrink-0">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                <div>
                  <strong class="d-block text-dark">{{ isEn ? 'Always Check is in Standby' : 'สถานะระบบ: สแตนด์บาย (Standby)' }}</strong>
                  <span class="text-muted">{{ isEn ? 'Always Check will automatically activate when Auto Sync is turned off. You can configure its schedule below.' : 'Always Check จะเริ่มทำงานอัตโนมัติทันทีเมื่อปิด Auto Sync โดยท่านสามารถตั้งค่ารอบเวลาเตรียมไว้ล่วงหน้าได้ที่นี่' }}</span>
                </div>
              </div>

              <div
                v-else
                class="alert alert-success py-2 px-3 mb-3 small rounded-lg border d-flex align-items-center"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="mr-2 text-success flex-shrink-0">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
                <div>
                  <strong class="d-block text-success">{{ isEn ? 'Always Check is Active' : 'สถานะระบบ: กำลังทำงาน (Active)' }}</strong>
                  <span class="text-muted">{{ isEn ? 'Actively detecting differences and updating notifications on schedule.' : 'คอยตรวจสอบความเปลี่ยนแปลงและแจ้งเตือนตามรอบเวลาที่กำหนด' }}</span>
                </div>
              </div>

              <!-- Frequency Mode Selection (DAY / MONTH / YEAR) -->
              <div class="mt-2">
                <label class="text-muted small font-weight-bold text-uppercase d-block mb-2">
                  {{ isEn ? 'Check Frequency' : 'รูปแบบความถี่การตรวจสอบ' }}
                </label>
                <div class="btn-group btn-group-sm w-100 mb-3" role="group">
                  <button
                    type="button"
                    class="btn"
                    :class="(settings.alwaysCheckMode || 'DAY') === 'DAY' ? 'btn-primary font-weight-bold' : 'btn-outline-secondary'"
                    @click="setAlwaysCheckMode('DAY')"
                  >
                    {{ $t('requesterOrgSync.modeDay') }}
                  </button>
                  <button
                    type="button"
                    class="btn"
                    :class="settings.alwaysCheckMode === 'MONTH' ? 'btn-primary font-weight-bold' : 'btn-outline-secondary'"
                    @click="setAlwaysCheckMode('MONTH')"
                  >
                    {{ $t('requesterOrgSync.modeMonth') }}
                  </button>
                  <button
                    type="button"
                    class="btn"
                    :class="settings.alwaysCheckMode === 'YEAR' ? 'btn-primary font-weight-bold' : 'btn-outline-secondary'"
                    @click="setAlwaysCheckMode('YEAR')"
                  >
                    {{ $t('requesterOrgSync.modeYear') }}
                  </button>
                </div>

                <!-- Parameters: DAY MODE -->
                <div v-if="(settings.alwaysCheckMode || 'DAY') === 'DAY'" class="p-3 bg-light rounded-lg border mb-3">
                  <CRow>
                    <CCol sm="6" class="mb-2 mb-sm-0">
                      <label class="small text-muted font-weight-bold d-block mb-1">
                        {{ $t('requesterOrgSync.everyNDays') }}
                      </label>
                      <input
                        type="number"
                        class="form-control form-control-sm"
                        min="1"
                        max="7"
                        v-model.number="settings.alwaysCheckIntervalDays"
                        @input="onAlwaysCheckParamChange"
                      />
                    </CCol>
                    <CCol sm="6">
                      <label class="small text-muted font-weight-bold d-block mb-1">
                        {{ $t('requesterOrgSync.syncTime') }}
                        <span class="badge badge-light border text-muted ml-1">{{ $t('requesterOrgSync.timeThaiLabel') }}</span>
                      </label>
                      <input
                        type="time"
                        class="form-control form-control-sm"
                        v-model="settings.alwaysCheckTime"
                        @input="onAlwaysCheckParamChange"
                      />
                    </CCol>
                  </CRow>
                </div>

                <!-- Parameters: MONTH MODE -->
                <div v-else-if="settings.alwaysCheckMode === 'MONTH'" class="p-3 bg-light rounded-lg border mb-3">
                  <CRow>
                    <CCol sm="6" class="mb-2 mb-sm-0">
                      <label class="small text-muted font-weight-bold d-block mb-1">
                        {{ $t('requesterOrgSync.dayOfMonth') }}
                      </label>
                      <input
                        type="number"
                        class="form-control form-control-sm"
                        min="1"
                        max="30"
                        v-model.number="settings.alwaysCheckDayOfMonth"
                        @input="onAlwaysCheckParamChange"
                      />
                    </CCol>
                    <CCol sm="6">
                      <label class="small text-muted font-weight-bold d-block mb-1">
                        {{ $t('requesterOrgSync.syncTime') }}
                        <span class="badge badge-light border text-muted ml-1">{{ $t('requesterOrgSync.timeThaiLabel') }}</span>
                      </label>
                      <input
                        type="time"
                        class="form-control form-control-sm"
                        v-model="settings.alwaysCheckTime"
                        @input="onAlwaysCheckParamChange"
                      />
                    </CCol>
                  </CRow>
                  <div v-if="settings.alwaysCheckDayOfMonth >= 29" class="text-muted small mt-2">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="mr-1">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="16" x2="12" y2="12"></line>
                      <line x1="12" y1="8" x2="12.01" y2="8"></line>
                    </svg>
                    {{ $t('requesterOrgSync.clampingNote') }}
                  </div>
                </div>

                <!-- Parameters: YEAR MODE -->
                <div v-else-if="settings.alwaysCheckMode === 'YEAR'" class="p-3 bg-light rounded-lg border mb-3">
                  <CRow>
                    <CCol sm="4" class="mb-2 mb-sm-0">
                      <label class="small text-muted font-weight-bold d-block mb-1">
                        {{ $t('requesterOrgSync.monthOfYear') }}
                      </label>
                      <select
                        class="form-control form-control-sm"
                        v-model.number="settings.alwaysCheckMonth"
                        @change="onAlwaysCheckParamChange"
                      >
                        <option v-for="m in thaiMonths" :key="m.value" :value="m.value">
                          {{ isEn ? m.nameEn : m.nameTh }}
                        </option>
                      </select>
                    </CCol>
                    <CCol sm="4" class="mb-2 mb-sm-0">
                      <label class="small text-muted font-weight-bold d-block mb-1">
                        {{ $t('requesterOrgSync.dayOfMonth') }}
                      </label>
                      <input
                        type="number"
                        class="form-control form-control-sm"
                        min="1"
                        max="30"
                        v-model.number="settings.alwaysCheckDayOfMonth"
                        @input="onAlwaysCheckParamChange"
                      />
                    </CCol>
                    <CCol sm="4">
                      <label class="small text-muted font-weight-bold d-block mb-1">
                        {{ $t('requesterOrgSync.syncTime') }}
                      </label>
                      <input
                        type="time"
                        class="form-control form-control-sm"
                        v-model="settings.alwaysCheckTime"
                        @input="onAlwaysCheckParamChange"
                      />
                    </CCol>
                  </CRow>
                  <div class="text-muted small mt-2">
                    <span class="badge badge-light border text-muted mr-1">{{ $t('requesterOrgSync.timeThaiLabel') }}</span>
                    <span v-if="settings.alwaysCheckDayOfMonth >= 29">{{ $t('requesterOrgSync.clampingNote') }}</span>
                  </div>
                </div>

                <!-- Live Calculated Preview Box for Always Check -->
                <div class="p-3 bg-white rounded-lg border shadow-sm mt-3">
                  <div class="d-flex justify-content-between align-items-center mb-1">
                    <span class="small font-weight-bold text-dark">{{ isEn ? 'Next Check Schedule Preview' : 'ตัวอย่างรอบตรวจสอบที่จะบันทึก:' }}</span>
                    <span class="badge badge-light text-muted small border">{{ isEn ? 'Unsaved Preview' : 'ตัวอย่างก่อนบันทึก' }}</span>
                  </div>
                  <div v-if="alwaysCheckSchedulePreview && alwaysCheckSchedulePreview.data" class="small">
                    <div class="font-weight-bold text-primary">
                      {{ alwaysCheckSchedulePreview.data.nextDayOfWeek }} ({{ formatDate(alwaysCheckSchedulePreview.data.nextScheduledSyncAt) }})
                    </div>
                    <div class="text-muted mt-1">{{ alwaysCheckSchedulePreview.data.explanation }}</div>
                    <div v-if="alwaysCheckSchedulePreview.data.wasClamped" class="badge badge-warning text-dark mt-1">
                      {{ isEn ? 'Adjusted to month-end' : 'ปรับเป็นวันสิ้นเดือนอัตโนมัติ' }}
                    </div>
                  </div>
                  <div v-else class="text-muted small">
                    {{ isEn ? 'Calculating check schedule...' : 'กำลังคำนวณตารางเวลา...' }}
                  </div>
                </div>

                <!-- Always Check Runtime Information & Status -->
                <div class="p-3 bg-light rounded-lg border mt-3 small">
                  <div class="d-flex justify-content-between py-1 border-bottom">
                    <span class="text-muted">{{ $t('requesterOrgSync.lastCheck') }}:</span>
                    <span class="font-weight-medium text-dark">{{ formatDate(status.lastCheckAt) }}</span>
                  </div>
                  <div class="d-flex justify-content-between py-1 border-bottom">
                    <span class="text-muted">{{ $t('requesterOrgSync.nextCheck') }}:</span>
                    <span class="font-weight-medium text-dark">{{ formatDate(status.nextScheduledCheckAt) }}</span>
                  </div>
                  <div class="d-flex justify-content-between py-1">
                    <span class="text-muted">{{ isEn ? 'Worker Status:' : 'สถานะการทำงาน:' }}</span>
                    <span v-if="settings.autoSyncEnabled" class="text-muted font-weight-medium">
                      {{ isEn ? 'Standby (Auto Sync Active)' : 'Standby (รอ Auto Sync ปิด)' }}
                    </span>
                    <span v-else-if="status.schedulerActive" class="text-success font-weight-bold">
                      {{ isEn ? 'Active (Running)' : 'Active (กำลังทำงาน)' }}
                    </span>
                    <span v-else class="text-secondary font-weight-medium">
                      {{ isEn ? 'Inactive' : 'ปิดการทำงาน' }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- SETTINGS ACTION BAR (SAVE BUTTON) -->
    <div class="d-flex justify-content-end mb-4">
      <CButton
        id="saveSettingsBtn"
        color="primary"
        class="sync-btn sync-btn--primary px-4 py-2 font-weight-bold shadow-sm"
        style="min-width: 200px; max-width: 260px;"
        :disabled="busy || !isIntervalValid"
        @click="saveSettings"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" class="mr-2">
          <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
          <polyline points="17 21 17 13 7 13 7 21"></polyline>
          <polyline points="7 3 7 8 15 8"></polyline>
        </svg>
        <span>{{ savingSettings ? $t('requesterOrgSync.saving') : $t('requesterOrgSync.saveSettings') }}</span>
      </CButton>
    </div>

    <!-- SECTION 5: COLLAPSIBLE TECHNICAL AUDIT DETAILS -->
    <CCard class="sync-card mb-4 border-0 shadow-sm">
      <CCardHeader
        class="sync-card-header bg-white d-flex justify-content-between align-items-center cursor-pointer"
        @click="showTechnicalDetails = !showTechnicalDetails"
        style="cursor: pointer;"
      >
        <div class="d-flex align-items-center">
          <span class="sync-card-badge-icon mr-2 text-secondary">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="16 18 22 12 16 6"></polyline>
              <polyline points="8 6 2 12 8 18"></polyline>
            </svg>
          </span>
          <span class="sync-card-title font-weight-bold">{{ $t('requesterOrgSync.technicalDetails') }}</span>
        </div>
        <button
          type="button"
          class="btn btn-sm btn-link text-muted p-0 text-decoration-none"
          @click.stop="showTechnicalDetails = !showTechnicalDetails"
        >
          {{ showTechnicalDetails ? $t('requesterOrgSync.hideDetails') : $t('requesterOrgSync.showDetails') }}
        </button>
      </CCardHeader>

      <CCardBody v-show="showTechnicalDetails" class="sync-card-body pt-3">
        <div class="d-flex flex-wrap justify-content-between text-muted small pb-2 border-bottom mb-3">
          <div class="mr-3 mb-1">
            <strong>{{ $t('requesterOrgSync.planId') }}:</strong>
            <span class="font-monospace text-dark ml-1">{{ short(result ? result.approvedPlanId : null) }}</span>
          </div>
          <div class="mr-3 mb-1">
            <strong>{{ $t('requesterOrgSync.planHash') }}:</strong>
            <span class="font-monospace text-dark ml-1">{{ short(result ? result.reviewedPlanHash : null) }}</span>
          </div>
          <div class="mb-1">
            <strong>{{ $t('requesterOrgSync.sourceFingerprint') }}:</strong>
            <span class="font-monospace text-dark ml-1">{{ short(result ? result.sourceSnapshotHash : status.comparisonFingerprint) }}</span>
          </div>
        </div>

        <!-- Decision Metrics breakdown if present -->
        <div v-if="status.decisionMetrics" class="p-3 bg-light rounded-lg border mb-2 small">
          <h6 class="font-weight-bold text-dark mb-2">Decision Metrics</h6>
          <div class="row">
            <div class="col-6 col-sm-3 mb-1"><strong>New:</strong> {{ status.decisionMetrics.createNewCount }}</div>
            <div class="col-6 col-sm-3 mb-1"><strong>Conflicts:</strong> {{ status.decisionMetrics.conflictCount }}</div>
            <div class="col-6 col-sm-3 mb-1"><strong>Name Drift:</strong> {{ status.decisionMetrics.nameDriftCount }}</div>
            <div class="col-6 col-sm-3 mb-1"><strong>Missing:</strong> {{ status.decisionMetrics.missingFromSourceCount }}</div>
          </div>
        </div>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import dialogAccessibility from '@/directives/dialogAccessibility'
import Swal from 'sweetalert2'
import Service from '@/service/api'
import { getSocket } from '@/service/socketio.service'

export default {
  name: 'RequesterOrganizationSync',
  directives: { dialogAccessibility },
  props: {
    autoLoadSettings: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    state: 'IDLE',
    result: null,
    savingSettings: false,
    checkingNow: false,
    syncingNow: false,
    latestRequestId: 0,
    showScheduleSettings: true,
    showTechnicalDetails: false,
    savedAutoSyncEnabled: false,
    savedSettings: null,
    settings: {
      alwaysCheckEnabled: true,
      autoSyncEnabled: false,
      intervalValue: 1,
      intervalUnit: 'DAY',
      checkIntervalDays: 1,
      autoSyncMode: 'DAY',
      autoSyncIntervalDays: 1,
      autoSyncDayOfMonth: 1,
      autoSyncMonth: 1,
      autoSyncTime: '09:00',
      alwaysCheckMode: 'DAY',
      alwaysCheckIntervalDays: 1,
      alwaysCheckDayOfMonth: 1,
      alwaysCheckMonth: 1,
      alwaysCheckTime: '09:00',
    },
    status: {
      lastCheckAt: null,
      lastCheckStatus: null,
      lastSuccessfulSyncAt: null,
      nextScheduledCheckAt: null,
      nextScheduledSyncAt: null,
      lastAutoSyncAt: null,
      masterDataStatus: 'UNKNOWN',
      lastSafeErrorCode: null,
      reviewRequired: false,
      schedulerActive: false,
      automaticApplyActive: false,
      autoSyncSchedulerActive: false,
      schedulerWarning: null,
      comparisonFingerprint: null,
      lastDecisionAt: null,
      autoSyncDecision: null,
      autoSyncDecisionReasonCode: null,
      allRequiredMetricsKnown: false,
      decisionMetrics: null,
    },
    schedulePreview: {
      data: null,
      error: null,
    },
    alwaysCheckSchedulePreview: {
      data: null,
      error: null,
    },
    pendingReview: null,
    loadingPendingReview: false,
    activeTab: 'UPDATE_ORG',
    recentUpdates: [],
    loadingRecentUpdates: false,
    revertingLogId: null,
    selectedItemKeys: [],
    codeMappings: {},
    showCodeDriftModal: false,
    activeCodeDriftItem: null,
    approvingReview: false,
    postponingReview: false,
    verifyingHr: false,
    verifyingItemKey: null,
    acknowledgingItemKey: null,
    postponingItemKey: null,
    resumingItemKey: null,
    thaiMonths: [
      { value: 1, nameTh: 'มกราคม', nameEn: 'January' },
      { value: 2, nameTh: 'กุมภาพันธ์', nameEn: 'February' },
      { value: 3, nameTh: 'มีนาคม', nameEn: 'March' },
      { value: 4, nameTh: 'เมษายน', nameEn: 'April' },
      { value: 5, nameTh: 'พฤษภาคม', nameEn: 'May' },
      { value: 6, nameTh: 'มิถุนายน', nameEn: 'June' },
      { value: 7, nameTh: 'กรกฎาคม', nameEn: 'July' },
      { value: 8, nameTh: 'สิงหาคม', nameEn: 'August' },
      { value: 9, nameTh: 'กันยายน', nameEn: 'September' },
      { value: 10, nameTh: 'ตุลาคม', nameEn: 'October' },
      { value: 11, nameTh: 'พฤศจิกายน', nameEn: 'November' },
      { value: 12, nameTh: 'ธันวาคม', nameEn: 'December' },
    ],
    historyItems: [],
    historyPagination: {
      page: 1,
      limit: 10,
      pageSize: 10,
      total: 0,
      totalItems: 0,
      totalPages: 1,
      hasNextPage: false,
      hasPrevPage: false,
    },
    historyLoading: false,
    historySearch: '',
    historyActionType: '',
    historyStartDate: '',
    historyEndDate: '',
  }),
  computed: {
    busy() {
      return (
        this.savingSettings ||
        this.checkingNow ||
        this.syncingNow ||
        this.loadingPendingReview ||
        this.approvingReview ||
        this.state === 'SYNC_NOW_LOADING' ||
        ['PREVIEW_LOADING', 'APPLY_CONFIRM', 'APPLY_LOADING'].includes(this.state)
      )
    },
    isEn() {
      return Boolean(this.$store && this.$store.state && this.$store.state.locale && this.$store.state.locale.lang === 'en')
    },
    pendingTotalCount() {
      if (!this.pendingReview?.items) return 0
      return this.pendingReview.items.filter(i =>
        !['APPROVED', 'ACKNOWLEDGED_KEEP_DEFAULT'].includes(i.status)
      ).length
    },
    pendingCreateItems() {
      if (!this.pendingReview?.items) return []
      return this.pendingReview.items.filter(i =>
        !['APPROVED', 'ACKNOWLEDGED_KEEP_DEFAULT'].includes(i.status) &&
        (i.category === 'CREATE_NEW' || i.changeType === 'CREATE_NEW' || i.changeType === 'NEW_ORGANIZATION')
      )
    },
    pendingUpdateItems() {
      if (!this.pendingReview?.items) return []
      return this.pendingReview.items.filter(i =>
        !['APPROVED', 'ACKNOWLEDGED_KEEP_DEFAULT'].includes(i.status) &&
        (i.category === 'UPDATE_NAME' || i.changeType === 'UPDATE_NAME' || i.changeType === 'NAME_CHANGE')
      )
    },
    pendingReviewItems() {
      if (!this.pendingReview?.items) return []
      return this.pendingReview.items.filter(i =>
        !['APPROVED', 'ACKNOWLEDGED_KEEP_DEFAULT'].includes(i.status) && (
          ['CODE_DRIFT', 'MISSING_FROM_SOURCE', 'COLLISION', 'AMBIGUOUS'].includes(i.category) ||
          ['CODE_DRIFT', 'MISSING_FROM_SOURCE', 'CODE_CHANGE', 'MISSING_IN_MASTER', 'COLLISION', 'AMBIGUOUS'].includes(i.changeType) ||
          !i.eligibleForApproval ||
          (Array.isArray(i.blockers) && i.blockers.length > 0)
        )
      )
    },
    selectedCreateCount() {
      const selectedSet = new Set(this.selectedItemKeys)
      return this.pendingCreateItems.filter(
        i => selectedSet.has(i.itemKey) && this.isItemEligibleForApproval(i)
      ).length
    },
    selectedNameUpdateCount() {
      const selectedSet = new Set(this.selectedItemKeys)
      return this.pendingUpdateItems.filter(
        i => selectedSet.has(i.itemKey) && this.isItemEligibleForApproval(i)
      ).length
    },
    selectedCodeUpdateCount() {
      const selectedSet = new Set(this.selectedItemKeys)
      return (this.pendingReview?.items || []).filter(i => {
        const isCodeDrift = i.category === 'CODE_DRIFT' || i.changeType === 'CODE_DRIFT' || i.changeType === 'CODE_CHANGE'
        return isCodeDrift && selectedSet.has(i.itemKey) && this.isItemEligibleForApproval(i)
      }).length
    },
    selectedUpdateCount() {
      return this.selectedNameUpdateCount + this.selectedCodeUpdateCount
    },
    selectedTotalCount() {
      return this.selectedCreateCount + this.selectedUpdateCount
    },
    areAllCreateSelected() {
      const eligible = this.pendingCreateItems.filter(i => this.isItemEligibleForApproval(i))
      if (eligible.length === 0) return false
      return eligible.every(i => this.selectedItemKeys.includes(i.itemKey))
    },
    areAllUpdateSelected() {
      const eligible = this.pendingUpdateItems.filter(i => this.isItemEligibleForApproval(i))
      if (eligible.length === 0) return false
      return eligible.every(i => this.selectedItemKeys.includes(i.itemKey))
    },
    statusBannerType() {
      if (
        this.status.lastCheckStatus === 'SUCCESS' &&
        (this.status.masterDataStatus === 'UP_TO_DATE' || this.status.autoSyncDecision === 'UP_TO_DATE') &&
        !this.status.reviewRequired &&
        this.pendingTotalCount === 0 &&
        (!this.status.decisionMetrics || (
          (this.status.decisionMetrics.createNewCount || 0) === 0 &&
          (this.status.decisionMetrics.conflictCount || 0) === 0 &&
          (this.status.decisionMetrics.nameDriftCount || 0) === 0 &&
          (this.status.decisionMetrics.missingFromSourceCount || 0) === 0
        ))
      ) {
        return 'GREEN'
      }

      if (
        this.status.lastCheckStatus === 'SUCCESS' &&
        (
          this.status.reviewRequired ||
          this.pendingTotalCount > 0 ||
          this.status.masterDataStatus === 'UPDATE_AVAILABLE' ||
          this.status.masterDataStatus === 'REVIEW_REQUIRED' ||
          this.status.autoSyncDecision === 'SAFE_CREATE_NEW' ||
          this.status.autoSyncDecision === 'REVIEW_REQUIRED' ||
          this.status.autoSyncDecision === 'BLOCKED' ||
          (this.status.decisionMetrics && (
            (this.status.decisionMetrics.createNewCount || 0) > 0 ||
            (this.status.decisionMetrics.conflictCount || 0) > 0 ||
            (this.status.decisionMetrics.nameDriftCount || 0) > 0 ||
            (this.status.decisionMetrics.missingFromSourceCount || 0) > 0
          ))
        )
      ) {
        return 'RED'
      }

      return 'NEUTRAL'
    },
    statusRedDetailsText() {
      const parts = []
      const metrics = this.status.decisionMetrics || (this.result?.summary ? {
        createNewCount: this.result.summary.createNew,
        conflictCount: this.result.summary.conflicts,
        nameDriftCount: this.result.summary.nameDrift,
        missingFromSourceCount: this.result.summary.missingFromSource,
      } : null)
      if (metrics) {
        if (metrics.createNewCount > 0) {
          parts.push(`${this.isEn ? 'New organizations' : 'หน่วยงานใหม่'}: ${metrics.createNewCount}`)
        }
        if (metrics.conflictCount > 0) {
          parts.push(`${this.isEn ? 'Conflicts' : 'ข้อมูลขัดแย้ง'}: ${metrics.conflictCount}`)
        }
        if (metrics.nameDriftCount > 0) {
          parts.push(`${this.isEn ? 'Name drift' : 'ชื่อเปลี่ยน'}: ${metrics.nameDriftCount}`)
        }
        if (metrics.missingFromSourceCount > 0) {
          parts.push(`${this.isEn ? 'Missing from source' : 'ไม่พบในต้นทาง'}: ${metrics.missingFromSourceCount}`)
        }
      }
      if (parts.length > 0) {
        return parts.join(' | ')
      }
      return this.isEn ? 'Changes detected that require review or sync' : 'พบรายการเปลี่ยนแปลงที่ต้องตรวจสอบหรือซิงค์'
    },
    reviewCount() {
      if (!this.result?.summary) return 0
      const { nameDrift = 0, missingFromSource = 0, quarantined = 0, conflicts = 0 } = this.result.summary
      return (nameDrift || 0) + (missingFromSource || 0) + (quarantined || 0) + (conflicts || 0)
    },
    intervalValidation() {
      const val = this.settings.intervalValue
      const unit = this.settings.intervalUnit
      if (val === null || val === undefined || val === '' || !Number.isInteger(Number(val)) || Number(val) <= 0) {
        return { valid: false, messageKey: 'requesterOrgSync.intervalRequired' }
      }
      const num = Number(val)
      if (unit === 'DAY' && (num < 1 || num > 365)) {
        return { valid: false, messageKey: 'requesterOrgSync.intervalMaxDay' }
      }
      if (unit === 'MONTH' && (num < 1 || num > 120)) {
        return { valid: false, messageKey: 'requesterOrgSync.intervalMaxMonth' }
      }
      if (unit === 'YEAR' && (num < 1 || num > 10)) {
        return { valid: false, messageKey: 'requesterOrgSync.intervalMaxYear' }
      }
      return { valid: true, messageKey: null }
    },
    isIntervalValid() {
      return this.intervalValidation.valid
    },
    intervalErrorMessage() {
      return this.intervalValidation.messageKey ? this.$t(this.intervalValidation.messageKey) : ''
    },
    statusBadgeClass() {
      if (this.status.lastCheckStatus === 'SUCCESS') return 'badge badge-success'
      if (this.status.lastCheckStatus === 'AUTH_FAILED') return 'badge badge-warning'
      if (this.status.lastCheckStatus === 'UNAVAILABLE') return 'badge badge-danger'
      return 'badge badge-secondary'
    },
    decisionBadgeClass() {
      if (this.status.autoSyncDecision === 'UP_TO_DATE') return 'badge badge-success'
      if (this.status.autoSyncDecision === 'SAFE_CREATE_NEW') return 'badge badge-info'
      if (this.status.autoSyncDecision === 'REVIEW_REQUIRED') return 'badge badge-warning'
      if (this.status.autoSyncDecision === 'BLOCKED') return 'badge badge-danger'
      return 'badge badge-secondary'
    },
    decisionText() {
      if (this.status.autoSyncDecision === 'UP_TO_DATE') return this.$t('requesterOrgSync.decisionUpToDate')
      if (this.status.autoSyncDecision === 'SAFE_CREATE_NEW') return this.$t('requesterOrgSync.decisionSafeCreateNew')
      if (this.status.autoSyncDecision === 'REVIEW_REQUIRED') return this.$t('requesterOrgSync.decisionReviewRequired')
      if (this.status.autoSyncDecision === 'BLOCKED') return this.$t('requesterOrgSync.decisionBlocked')
      return '-'
    },
  },
  mounted() {
    try {
      const socket = getSocket()
      if (socket) {
        this.statusUpdatedHandler = () => {
          this.loadSettings({ force: false })
          this.loadRecentUpdates()
          this.loadHistory(this.historyPagination.page || 1)
        }
        socket.on('blendata_status_updated', this.statusUpdatedHandler)
      }
    } catch (_) {}

    if (this.autoLoadSettings) {
      this.loadSettings()
      this.loadRecentUpdates()
      this.updateSchedulePreview()
      this.updateAlwaysCheckSchedulePreview()
      this.loadHistory(1)
    }
  },
  beforeDestroy() {
    if (this.statusUpdatedHandler) {
      try {
        const socket = getSocket()
        if (socket) {
          socket.off('blendata_status_updated', this.statusUpdatedHandler)
        }
      } catch (_) {}
    }
  },
  methods: {
    formatDate(value) {
      if (!value) return '-'
      try {
        const d = new Date(value)
        if (isNaN(d.getTime())) return value
        const isEn = this.isEn || (this.$i18n && this.$i18n.locale === 'en')
        const loc = isEn ? 'en-GB' : 'th-TH'
        return d.toLocaleString(loc, {
          timeZone: 'Asia/Bangkok',
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        }).replace(',', '')
      } catch {
        return value
      }
    },
    errorCode(error) {
      return error?.response?.data?.code || 'BLENDATA_SYNC_VALIDATION_FAILED'
    },
    short(value) {
      return value ? `${value.slice(0, 12)}…` : '-'
    },
    invalidate() {
      this.result = null
      this.state = 'ERROR'
    },
    setScheduleMode(mode) {
      this.settings.autoSyncMode = mode
      this.updateSchedulePreview()
    },
    onScheduleParamChange() {
      this.updateSchedulePreview()
    },
    setAlwaysCheckMode(mode) {
      this.settings.alwaysCheckMode = mode
      this.updateAlwaysCheckSchedulePreview()
    },
    onAlwaysCheckParamChange() {
      this.updateAlwaysCheckSchedulePreview()
    },
    async updateAlwaysCheckSchedulePreview() {
      const mode = this.settings.alwaysCheckMode || 'DAY'
      try {
        const payload = {
          mode,
          intervalDays: Number(this.settings.alwaysCheckIntervalDays) || 1,
          dayOfMonth: Number(this.settings.alwaysCheckDayOfMonth) || 1,
          month: Number(this.settings.alwaysCheckMonth) || 1,
          time: this.settings.alwaysCheckTime || '09:00',
          timeZone: 'Asia/Bangkok',
        }
        const response = await Service.requesterOrganizationSync('previewSchedule', payload)
        if (response?.data?.data) {
          this.alwaysCheckSchedulePreview = {
            data: response.data.data,
            error: null,
          }
        }
      } catch (err) {
        this.alwaysCheckSchedulePreview = {
          data: null,
          error: err?.response?.data?.message || err?.message,
        }
      }
    },
    async updateSchedulePreview() {
      const mode = this.settings.autoSyncMode || 'DAY'
      try {
        const payload = {
          mode,
          intervalDays: Number(this.settings.autoSyncIntervalDays) || 1,
          dayOfMonth: Number(this.settings.autoSyncDayOfMonth) || 1,
          month: Number(this.settings.autoSyncMonth) || 1,
          time: this.settings.autoSyncTime || '09:00',
          timeZone: 'Asia/Bangkok',
        }
        const response = await Service.requesterOrganizationSync('previewSchedule', payload)
        if (response?.data?.data) {
          this.schedulePreview = {
            data: response.data.data,
            error: null,
          }
        }
      } catch (err) {
        this.schedulePreview = {
          data: null,
          error: err?.response?.data?.message || err?.message,
        }
      }
    },
    isSettingsDirty() {
      if (!this.savedSettings) return false
      return (
        Boolean(this.settings.alwaysCheckEnabled) !== Boolean(this.savedSettings.alwaysCheckEnabled) ||
        Boolean(this.settings.autoSyncEnabled) !== Boolean(this.savedSettings.autoSyncEnabled) ||
        Number(this.settings.intervalValue) !== Number(this.savedSettings.intervalValue) ||
        String(this.settings.intervalUnit) !== String(this.savedSettings.intervalUnit) ||
        String(this.settings.autoSyncMode) !== String(this.savedSettings.autoSyncMode) ||
        Number(this.settings.autoSyncIntervalDays) !== Number(this.savedSettings.autoSyncIntervalDays) ||
        Number(this.settings.autoSyncDayOfMonth) !== Number(this.savedSettings.autoSyncDayOfMonth) ||
        Number(this.settings.autoSyncMonth) !== Number(this.savedSettings.autoSyncMonth) ||
        String(this.settings.autoSyncTime) !== String(this.savedSettings.autoSyncTime) ||
        String(this.settings.alwaysCheckMode) !== String(this.savedSettings.alwaysCheckMode) ||
        Number(this.settings.alwaysCheckIntervalDays) !== Number(this.savedSettings.alwaysCheckIntervalDays) ||
        Number(this.settings.alwaysCheckDayOfMonth) !== Number(this.savedSettings.alwaysCheckDayOfMonth) ||
        Number(this.settings.alwaysCheckMonth) !== Number(this.savedSettings.alwaysCheckMonth) ||
        String(this.settings.alwaysCheckTime) !== String(this.savedSettings.alwaysCheckTime)
      )
    },
    async loadSettings(options = {}) {
      const { force = false } = typeof options === 'boolean' ? { force: options } : options
      const requestId = ++this.latestRequestId
      try {
        const response = await Service.requesterOrganizationSync('getSettings')
        if (requestId !== this.latestRequestId) return
        if (response?.data?.data) {
          const d = response.data.data
          const isDirty = this.isSettingsDirty()

          this.savedAutoSyncEnabled = Boolean(d.autoSyncEnabled)
          this.status = {
            lastCheckAt: d.lastCheckAt || null,
            lastCheckStatus: d.lastCheckStatus || null,
            lastSuccessfulSyncAt: d.lastSuccessfulSyncAt || null,
            nextScheduledCheckAt: d.nextScheduledCheckAt || null,
            nextScheduledSyncAt: d.nextScheduledSyncAt || null,
            lastAutoSyncAt: d.lastAutoSyncAt || null,
            masterDataStatus: d.masterDataStatus || 'UNKNOWN',
            lastSafeErrorCode: d.lastSafeErrorCode || null,
            reviewRequired: Boolean(d.reviewRequired),
            schedulerActive: Boolean(d.schedulerActive),
            schedulerWarning: d.schedulerWarning || null,
            automaticApplyActive: Boolean(d.automaticApplyActive),
            autoSyncSchedulerActive: Boolean(d.autoSyncSchedulerActive),
            comparisonFingerprint: d.comparisonFingerprint || null,
            lastDecisionAt: d.lastDecisionAt || null,
            autoSyncDecision: d.autoSyncDecision || null,
            autoSyncDecisionReasonCode: d.autoSyncDecisionReasonCode || null,
            allRequiredMetricsKnown: Boolean(d.allRequiredMetricsKnown),
            decisionMetrics: d.decisionMetrics || null,
          }

          if (!isDirty || force || !this.savedSettings) {
            this.settings.alwaysCheckEnabled = Boolean(d.alwaysCheckEnabled)
            if (d.alwaysCheckMode) this.settings.alwaysCheckMode = d.alwaysCheckMode
            if (d.alwaysCheckIntervalDays) this.settings.alwaysCheckIntervalDays = d.alwaysCheckIntervalDays
            if (d.alwaysCheckDayOfMonth) this.settings.alwaysCheckDayOfMonth = d.alwaysCheckDayOfMonth
            if (d.alwaysCheckMonth) this.settings.alwaysCheckMonth = d.alwaysCheckMonth
            if (d.alwaysCheckTime) this.settings.alwaysCheckTime = d.alwaysCheckTime
            this.settings.autoSyncEnabled = Boolean(d.autoSyncEnabled)
            this.settings.intervalValue = d.intervalValue || 1
            this.settings.intervalUnit = d.intervalUnit || 'DAY'
            this.settings.checkIntervalDays = d.checkIntervalDays || d.intervalValue || 1

            if (d.autoSyncMode) {
              this.settings.autoSyncMode = d.autoSyncMode
            }
            if (d.autoSyncIntervalDays) {
              this.settings.autoSyncIntervalDays = d.autoSyncIntervalDays
            }
            if (d.autoSyncDayOfMonth) {
              this.settings.autoSyncDayOfMonth = d.autoSyncDayOfMonth
            }
            if (d.autoSyncMonth) {
              this.settings.autoSyncMonth = d.autoSyncMonth
            }
            if (d.autoSyncTime) {
              this.settings.autoSyncTime = d.autoSyncTime
            }

            this.savedSettings = {
              alwaysCheckEnabled: this.settings.alwaysCheckEnabled,
              autoSyncEnabled: this.settings.autoSyncEnabled,
              intervalValue: this.settings.intervalValue,
              intervalUnit: this.settings.intervalUnit,
              checkIntervalDays: this.settings.checkIntervalDays,
              autoSyncMode: this.settings.autoSyncMode,
              autoSyncIntervalDays: this.settings.autoSyncIntervalDays,
              autoSyncDayOfMonth: this.settings.autoSyncDayOfMonth,
              autoSyncMonth: this.settings.autoSyncMonth,
              autoSyncTime: this.settings.autoSyncTime,
              alwaysCheckMode: this.settings.alwaysCheckMode,
              alwaysCheckIntervalDays: this.settings.alwaysCheckIntervalDays,
              alwaysCheckDayOfMonth: this.settings.alwaysCheckDayOfMonth,
              alwaysCheckMonth: this.settings.alwaysCheckMonth,
              alwaysCheckTime: this.settings.alwaysCheckTime,
            }
          }
        }
      } catch {
        // Keep in-memory defaults on load error
      }
    },
    async saveSettings() {
      if (this.busy || !this.isIntervalValid) return
      this.savingSettings = true
      const requestId = ++this.latestRequestId
      try {
        const alwaysCheckDays = Number(this.settings.alwaysCheckIntervalDays) || Number(this.settings.intervalValue) || 1
        const payload = {
          alwaysCheckEnabled: true, // Always Check cannot be disabled
          alwaysCheckMode: this.settings.alwaysCheckMode || 'DAY',
          alwaysCheckIntervalDays: alwaysCheckDays,
          alwaysCheckDayOfMonth: Number(this.settings.alwaysCheckDayOfMonth) || 1,
          alwaysCheckMonth: Number(this.settings.alwaysCheckMonth) || 1,
          alwaysCheckTime: this.settings.alwaysCheckTime || '09:00',
          intervalValue: alwaysCheckDays,
          intervalUnit: this.settings.alwaysCheckMode || 'DAY',
          checkIntervalDays: alwaysCheckDays,
          autoSyncEnabled: this.settings.autoSyncEnabled,
          autoSyncMode: this.settings.autoSyncMode || 'DAY',
          autoSyncIntervalDays: Number(this.settings.autoSyncIntervalDays) || 1,
          autoSyncDayOfMonth: Number(this.settings.autoSyncDayOfMonth) || 1,
          autoSyncMonth: Number(this.settings.autoSyncMonth) || 1,
          autoSyncTime: this.settings.autoSyncTime || '09:00',
        }

        const response = await Service.requesterOrganizationSync('saveSettings', payload)
        if (requestId !== this.latestRequestId) return
        this.savedAutoSyncEnabled = Boolean(this.settings.autoSyncEnabled)
        this.savedSettings = {
          alwaysCheckEnabled: this.settings.alwaysCheckEnabled,
          autoSyncEnabled: this.settings.autoSyncEnabled,
          intervalValue: this.settings.intervalValue,
          intervalUnit: this.settings.intervalUnit,
          checkIntervalDays: this.settings.intervalValue || 1,
          autoSyncMode: this.settings.autoSyncMode,
          autoSyncIntervalDays: this.settings.autoSyncIntervalDays,
          autoSyncDayOfMonth: this.settings.autoSyncDayOfMonth,
          autoSyncMonth: this.settings.autoSyncMonth,
          autoSyncTime: this.settings.autoSyncTime,
          alwaysCheckMode: this.settings.alwaysCheckMode,
          alwaysCheckIntervalDays: this.settings.alwaysCheckIntervalDays,
          alwaysCheckDayOfMonth: this.settings.alwaysCheckDayOfMonth,
          alwaysCheckMonth: this.settings.alwaysCheckMonth,
          alwaysCheckTime: this.settings.alwaysCheckTime,
        }
        if (response?.data?.data) {
          const d = response.data.data
          this.status.nextScheduledCheckAt = d.nextScheduledCheckAt || null
          this.status.nextScheduledSyncAt = d.nextScheduledSyncAt || null
          this.status.schedulerActive = Boolean(d.schedulerActive)
          this.status.schedulerWarning = d.schedulerWarning || null
          if (d.automaticApplyActive !== undefined) {
            this.status.automaticApplyActive = Boolean(d.automaticApplyActive)
          }
        }
        if (this.settings.autoSyncEnabled) {
          await Swal.fire({
            icon: 'success',
            title: this.isEn ? 'Settings Saved' : 'บันทึกการตั้งค่าสำเร็จ!',
            text: this.isEn
              ? 'Always Check schedule has been saved. It will operate on this schedule once Auto Sync is disabled (currently in Standby backup mode).'
              : 'บันทึกการตั้งค่าสำเร็จ! Always Check จะทำงานตามกำหนดการนี้เมื่อ Auto Sync ถูกปิดการใช้งาน (ปัจจุบันอยู่ในโหมด Standby สำรอง)',
          })
        } else {
          await Swal.fire({
            icon: 'success',
            title: this.isEn ? 'Settings Saved' : 'บันทึกการตั้งค่าสำเร็จ!',
            text: this.isEn
              ? 'Always Check is now active and will verify master data on the configured schedule.'
              : 'บันทึกการตั้งค่าสำเร็จ! Always Check เปิดทำงานตามกำหนดการที่ตั้งไว้เรียบร้อยแล้ว',
          })
        }
      } catch (error) {
        if (requestId !== this.latestRequestId) return
        await Swal.fire({
          icon: 'error',
          title: this.$t('requesterOrgSync.saveSettingsFailed'),
          text: `${this.$t('requesterOrgSync.errorCode')}: ${this.errorCode(error)}`,
        })
      } finally {
        this.savingSettings = false
      }
    },
    async loadRecentUpdates() {
      this.loadingRecentUpdates = true
      try {
        const response = await Service.requesterOrganizationSync('getRecentUpdates')
        const data = response?.data?.data
        if (data && Array.isArray(data.items)) {
          this.recentUpdates = data.items
        }
      } catch (err) {
        console.warn('Failed to load recent updates:', err)
      } finally {
        this.loadingRecentUpdates = false
      }
    },
    async confirmRevert(item) {
      if (!item || !item.id || this.revertingLogId) return
      const confirm = await Swal.fire({
        icon: 'warning',
        title: this.isEn ? 'Confirm Revert Affiliation?' : 'ยืนยันยกเลิกและคืนค่าสังกัดเดิม?',
        html: `<div class="text-left small">
          <p class="mb-2">${this.isEn ? 'Revert affiliation code back to previous name:' : 'ต้องการเปลี่ยนค่าสังกัดของรหัสนี้กลับเป็นชื่อเดิมก่อนหน้า:'}</p>
          <div class="p-2 bg-light rounded border mb-2 font-monospace">
            <strong>${item.code}</strong>: ${item.name}
            ${item.previousName ? `<div class="text-primary mt-1">คืนค่าเป็น: <b>${item.previousName}</b></div>` : '<div class="text-danger mt-1">ปิดการใช้งานสังกัดนี้</div>'}
          </div>
        </div>`,
        showCancelButton: true,
        confirmButtonText: this.isEn ? 'Confirm Revert' : 'ยืนยันยกเลิก',
        cancelButtonText: this.isEn ? 'Cancel' : 'ยกเลิก',
        confirmButtonColor: '#dc2626',
        cancelButtonColor: '#64748b',
      })
      if (!confirm.isConfirmed) return

      this.revertingLogId = item.id
      try {
        const res = await Service.requesterOrganizationSync('revert', { logId: item.id })
        if (res?.data?.success) {
          await Swal.fire({
            icon: 'success',
            title: this.isEn ? 'Reverted' : 'คืนค่าสังกัดสำเร็จ',
            text: res.data.data?.message || (this.isEn ? 'Reverted successfully' : 'คืนค่าสังกัดเดิมเรียบร้อยแล้ว'),
          })
          await this.loadRecentUpdates()
          await this.loadHistory(1)
        }
      } catch (err) {
        await Swal.fire({
          icon: 'error',
          title: this.isEn ? 'Revert Failed' : 'การคืนค่าล้มเหลว',
          text: err?.response?.data?.message || err.message,
        })
      } finally {
        this.revertingLogId = null
      }
    },
    async syncNow() {
      if (this.busy) return
      this.syncingNow = true
      this.state = 'SYNC_NOW_LOADING'
      const requestId = ++this.latestRequestId
      try {
        // ALERT 1: Ask confirmation to check differences
        const confirmation = await Swal.fire({
          icon: 'question',
          title: this.isEn ? 'Confirm Sync data now?' : 'ยืนยันว่าจะ Sync data now หรือไม่?',
          text: this.isEn
            ? 'The system will compare the database with University Master Data.'
            : 'ระบบจะทำการตรวจสอบความเปลี่ยนแปลงระหว่างฐานข้อมูลและ Master Data ของมหาวิทยาลัย',
          showCancelButton: true,
          confirmButtonText: this.isEn ? 'Sync data now' : 'Sync data now',
          cancelButtonText: this.isEn ? 'Cancel' : 'ยกเลิก',
          confirmButtonColor: '#2563eb',
          cancelButtonColor: '#64748b',
        })
        if (!confirmation.isConfirmed) {
          this.state = 'IDLE'
          this.syncingNow = false
          return
        }

        const response = await Service.requesterOrganizationSync('syncNow')
        if (requestId !== this.latestRequestId) return

        const outcome = response?.data?.data
        if (outcome?.status === 'UP_TO_DATE' || !outcome?.hasChanges || outcome?.count === 0) {
          await Swal.fire({
            icon: 'success',
            title: this.isEn ? 'Up to Date' : 'ข้อมูลเป็นปัจจุบันแล้ว',
            text: this.isEn ? 'All affiliations match University Master Data. No changes detected.' : (outcome?.message || 'ไม่พบรายการเปลี่ยนแปลงระหว่างฐานข้อมูลและ Master Data ของมหาวิทยาลัย'),
          })
          await this.loadSettings()
        } else if (outcome?.status === 'CHANGES_DETECTED' && outcome?.changes && outcome?.changes.length > 0) {
          // ALERT 2: Details Modal Popup with scrollable container
          const changes = outcome.changes || []
          const changesCount = changes.length
          const rowsHtml = changes.map(item => {
            const code = item.code || '-'
            const prevName = item.previousName ? `<del style="color: #64748b; font-size: 12px;">${item.previousName}</del><br>` : ''
            const newName = `<span style="color: #16a34a; font-weight: 600;">${item.name}</span>`
            const badgeClass = item.changeType === 'CREATE_NEW' ? 'badge-info' : 'badge-primary'
            const typeText = item.changeType === 'CREATE_NEW' ? (this.isEn ? 'New Unit' : 'สร้างใหม่') : (this.isEn ? 'Update Name' : 'เปลี่ยนชื่อ')

            return `
              <tr>
                <td style="padding: 7px 10px; font-family: monospace; font-size: 12px; font-weight: 600; vertical-align: middle;">${code}</td>
                <td style="padding: 7px 10px; font-size: 13px; vertical-align: middle;">${prevName}${newName}</td>
                <td style="padding: 7px 10px; text-align: center; vertical-align: middle;">
                  <span class="badge ${badgeClass}" style="font-size: 11px; padding: 4px 8px;">${typeText}</span>
                </td>
              </tr>
            `
          }).join('')

          const detailHtml = `
            <div style="text-align: left; margin-bottom: 12px;">
              <p style="margin: 0 0 6px 0; font-size: 14px; color: #334155;">
                ${this.isEn 
                  ? `Detected <b>${changesCount}</b> affiliation change(s) from University Master Data:` 
                  : `ตรวจพบการเปลี่ยนแปลงสังกัดจาก Master Data ทั้งหมด <b>${changesCount}</b> รายการ ดังนี้:`}
              </p>
            </div>
            <div style="max-height: 380px; overflow-y: auto; border: 1px solid #e2e8f0; border-radius: 8px; box-shadow: inset 0 1px 3px rgba(0,0,0,0.05);">
              <table class="table table-sm table-hover mb-0" style="width: 100%; text-align: left;">
                <thead style="position: sticky; top: 0; background: #f8fafc; border-bottom: 2px solid #e2e8f0; z-index: 10;">
                  <tr>
                    <th style="padding: 8px 10px; font-size: 12px; font-weight: 700; width: 25%; color: #475569;">${this.isEn ? 'Code' : 'รหัสสังกัด'}</th>
                    <th style="padding: 8px 10px; font-size: 12px; font-weight: 700; width: 55%; color: #475569;">${this.isEn ? 'Previous → New Name' : 'ชื่อเดิม → ชื่อใหม่'}</th>
                    <th style="padding: 8px 10px; font-size: 12px; font-weight: 700; width: 20%; text-align: center; color: #475569;">${this.isEn ? 'Type' : 'ประเภท'}</th>
                  </tr>
                </thead>
                <tbody>
                  ${rowsHtml}
                </tbody>
              </table>
            </div>
            <div style="text-align: left; margin-top: 10px;">
              <small style="color: #64748b;">
                ${this.isEn 
                  ? 'Click "Confirm" to apply and save these changes to the database directly.' 
                  : '* หากกดยืนยัน ระบบจะทำการบันทึกและปรับปรุงข้อมูลลงฐานข้อมูลทันที หากกดยกเลิกจะไม่เกิดการเปลี่ยนแปลงใดๆ'}
              </small>
            </div>
          `

          const detailConfirmation = await Swal.fire({
            title: this.isEn ? 'Detected Changes Details' : 'รายละเอียดการเปลี่ยนแปลงสังกัด',
            html: detailHtml,
            width: '780px',
            icon: 'info',
            showCancelButton: true,
            confirmButtonText: this.isEn ? 'Confirm (Apply to DB)' : 'ยืนยัน (บันทึกลงฐานข้อมูล)',
            cancelButtonText: this.isEn ? 'Cancel' : 'ยกเลิก',
            confirmButtonColor: '#16a34a',
            cancelButtonColor: '#64748b',
            focusConfirm: false,
          })

          if (detailConfirmation.isConfirmed) {
            this.state = 'SYNC_NOW_LOADING'
            const applyRes = await Service.requesterOrganizationSync('manualApply', { changes })
            if (applyRes?.data?.success) {
              await Swal.fire({
                icon: 'success',
                title: this.isEn ? 'Sync Completed' : 'บันทึกข้อมูลเรียบร้อยแล้ว',
                text: this.isEn
                  ? `Successfully applied ${changesCount} change(s) to the database.`
                  : `บันทึกการเปลี่ยนแปลงสังกัด ${changesCount} รายการลงในฐานข้อมูลเรียบร้อยแล้ว`,
              })
              await this.loadRecentUpdates()
              await this.loadHistory(1)
              if (this.autoLoadSettings) {
                await this.loadSettings()
              }
            }
          }
        } else {
          await Swal.fire({
            icon: 'warning',
            title: this.isEn ? 'Status Unknown' : 'ไม่สามารถยืนยันสถานะได้',
            text: this.isEn ? 'No comparison data available.' : (outcome?.message || 'ไม่สามารถตรวจสอบความเปลี่ยนแปลงได้ กรุณาลองใหม่อีกครั้ง'),
          })
        }

        if (this.autoLoadSettings) {
          await this.loadSettings()
        }
        this.state = 'SUCCESS'
      } catch (error) {
        if (requestId !== this.latestRequestId) return
        this.state = 'ERROR'
        await Swal.fire({
          icon: 'error',
          title: this.$t('requesterOrgSync.applyFailed'),
          text: `${this.$t('requesterOrgSync.errorCode')}: ${this.errorCode(error)}`,
        })
      } finally {
        this.syncingNow = false
        if (this.state === 'SYNC_NOW_LOADING') {
          this.state = 'IDLE'
        }
      }
    },
    async checkNow() {
      if (this.busy) return
      this.checkingNow = true
      const requestId = ++this.latestRequestId
      try {
        const response = await Service.requesterOrganizationSync('check')
        if (requestId !== this.latestRequestId) return
        const d = response?.data?.data
        if (d) {
          this.status.lastCheckAt = d.checkedAt
          this.status.lastCheckStatus = d.checkStatus
          this.status.masterDataStatus = d.masterDataStatus
          this.status.reviewRequired = d.reviewRequired
          this.status.lastSafeErrorCode = d.lastSafeErrorCode
          if (d.schedulerActive !== undefined) {
            this.status.schedulerActive = Boolean(d.schedulerActive)
          }
          if (d.automaticApplyActive !== undefined) {
            this.status.automaticApplyActive = Boolean(d.automaticApplyActive)
          }
          if (d.decision) {
            this.status.comparisonFingerprint = d.decision.comparisonFingerprint || null
            this.status.lastDecisionAt = d.decision.evaluatedAt || null
            this.status.autoSyncDecision = d.decision.decision || null
            this.status.autoSyncDecisionReasonCode = d.decision.decisionReasonCode || null
            this.status.allRequiredMetricsKnown = Boolean(d.decision.allRequiredMetricsKnown)
            this.status.decisionMetrics = d.decision.metrics || null
          }
          if (d.pendingReview) {
            this.pendingReview = d.pendingReview
          }
        }
        await Swal.fire({
          icon: 'success',
          title: this.$t('requesterOrgSync.checkSuccess'),
          text: `${this.$t('requesterOrgSync.masterDataStatus')}: ${d?.masterDataStatus || 'UNKNOWN'}`,
        })
      } catch (error) {
        if (requestId !== this.latestRequestId) return
        await Swal.fire({
          icon: 'error',
          title: this.$t('requesterOrgSync.checkFailed'),
          text: `${this.$t('requesterOrgSync.errorCode')}: ${this.errorCode(error)}`,
        })
      } finally {
        this.checkingNow = false
      }
    },
    async preview() {
      if (this.busy) return
      this.state = 'PREVIEW_LOADING'
      this.result = null
      const requestId = ++this.latestRequestId
      try {
        const response = await Service.requesterOrganizationSync('preview')
        if (requestId !== this.latestRequestId) return
        this.result = response.data.data
        this.state = 'PREVIEW_READY'
        if (this.result.upToDate) {
          await Swal.fire({
            icon: 'success',
            title: this.$t('requesterOrgSync.upToDate'),
            text: this.$t('requesterOrgSync.noApplyNeeded'),
          })
        }
      } catch (error) {
        if (requestId !== this.latestRequestId) return
        this.invalidate()
        await Swal.fire({
          icon: 'error',
          title: this.$t('requesterOrgSync.previewFailed'),
          text: `${this.$t('requesterOrgSync.errorCode')}: ${this.errorCode(error)}`,
        })
      } finally {
        if (this.state === 'PREVIEW_LOADING') {
          this.state = 'IDLE'
        }
      }
    },
    async confirmApply() {
      if (this.busy || !this.result?.applyEligible) return
      this.state = 'APPLY_CONFIRM'
      const createCount = this.result?.summary?.createNew || 0
      const confirmation = await Swal.fire({
        icon: 'warning',
        title: this.$t('requesterOrgSync.confirmAddTitle'),
        text: this.$t('requesterOrgSync.confirmAddText', { count: createCount }),
        showCancelButton: true,
        confirmButtonText: this.$t('requesterOrgSync.applyNowBtn'),
        cancelButtonText: this.$t('common.cancel'),
      })
      if (!confirmation.isConfirmed) {
        this.state = 'PREVIEW_READY'
        return
      }
      this.state = 'APPLY_LOADING'
      const requestId = ++this.latestRequestId
      try {
        const response = await Service.requesterOrganizationSync('apply', {
          approvedPlanId: this.result.approvedPlanId,
          reviewedPlanHash: this.result.reviewedPlanHash,
          explicitApply: true,
        })
        if (requestId !== this.latestRequestId) return
        if (!response?.data?.success) throw new Error('apply failed')

        let refreshWarning = response.data.data?.statusUpdateWarning || null
        if (this.autoLoadSettings) {
          try {
            await this.loadSettings()
          } catch {
            refreshWarning = 'SETTINGS_REFRESH_FAILED'
          }
        }

        this.result = null
        this.state = 'SUCCESS'

        if (refreshWarning) {
          await Swal.fire({
            icon: 'warning',
            title: this.$t('requesterOrgSync.completed'),
            text: `${this.$t('requesterOrgSync.added', { count: createCount })} (${this.$t('requesterOrgSync.refreshStatusWarning')})`,
          })
        } else {
          await Swal.fire({
            icon: 'success',
            title: this.$t('requesterOrgSync.completed'),
            text: this.$t('requesterOrgSync.added', { count: createCount }),
          })
        }
      } catch (error) {
        if (requestId !== this.latestRequestId) return
        if (
          [
            'SYNC_PLAN_STALE',
            'PLAN_HASH_MISMATCH',
            'SYNC_EVIDENCE_MISSING',
            'SYNC_EVIDENCE_HASH_MISMATCH',
            'SYNC_EVIDENCE_INVALID',
            'CONTROLLED_APPLY_DISABLED',
            'TARGET_GUARD_BLOCKED',
            'PLAN_ALREADY_APPLIED',
            'PLAN_INVALIDATED',
          ].includes(this.errorCode(error))
        ) {
          this.invalidate()
        } else {
          this.state = 'ERROR'
        }
        await Swal.fire({
          icon: 'error',
          title: this.$t('requesterOrgSync.applyFailed'),
          text: `${this.$t('requesterOrgSync.errorCode')}: ${this.errorCode(error)}`,
        })
      }
    },
    async loadPendingReview(reviewId = null) {
      this.loadingPendingReview = true
      try {
        const params = reviewId ? { reviewId } : {}
        const response = await Service.requesterOrganizationSync('getPendingReview', null, { params })
        let reviewObj = response?.data?.data?.pendingReview || (response?.data?.data?.items ? response.data.data : null)

        // If URL requested an old STALE review, switch to current active PENDING review if available
        if (reviewObj && reviewObj.status === 'STALE' && reviewId) {
          try {
            const activeResp = await Service.requesterOrganizationSync('getPendingReview')
            const activeReview = activeResp?.data?.data?.pendingReview || (activeResp?.data?.data?.items ? activeResp.data.data : null)
            if (activeReview && (activeReview.status === 'PENDING' || activeReview.status === 'PARTIALLY_APPLIED')) {
              reviewObj = activeReview
              if (this.$router && this.$route && this.$route.query.reviewId !== activeReview.reviewId) {
                this.$router.replace({ query: { ...this.$route.query, reviewId: activeReview.reviewId } }).catch(() => {})
              }
            }
          } catch (_) {}
        }

        if (reviewObj) {
          this.pendingReview = reviewObj
          this.pruneSelectedItemKeys()

          if (this.activeTab !== 'CHANGE_HISTORY' && this.pendingCreateItems.length === 0) {
            if (this.pendingUpdateItems.length > 0) {
              this.activeTab = 'UPDATE_NAME'
            } else if (this.pendingReviewItems.length > 0) {
              this.activeTab = 'REQUIRES_REVIEW'
            }
          }
          if (this.selectedItemKeys.length === 0) {
            this.selectAllEligible()
          }
        } else {
          this.pendingReview = null
          this.selectedItemKeys = []
        }
      } catch (err) {
        console.error('Failed to load pending review:', err)
      } finally {
        this.loadingPendingReview = false
      }
    },
    getItemCode(item) {
      return item?.organizationCode || item?.code || item?.existingUnit?.code || item?.masterDataUnit?.code || item?.targetData?.code || item?.sourceData?.code || '-'
    },
    getExistingName(item) {
      return item?.existingUnit?.name || item?.beforeValue || item?.targetData?.name || item?.organizationName || '-'
    },
    getMasterName(item) {
      return item?.masterDataUnit?.name || item?.afterValue || item?.organizationName || item?.sourceData?.name || '-'
    },
    getMasterCode(item) {
      return item?.masterDataUnit?.code || item?.organizationCode || item?.code || item?.sourceData?.code || '-'
    },
    pruneSelectedItemKeys() {
      if (!this.pendingReview?.items || this.pendingReview?.status === 'STALE') {
        this.selectedItemKeys = []
        return
      }
      const eligibleKeysSet = new Set(
        this.pendingReview.items
          .filter(i => this.isItemEligibleForApproval(i))
          .map(i => i.itemKey)
      )
      const nextKeys = this.selectedItemKeys.filter(k => eligibleKeysSet.has(k))
      if (nextKeys.length !== this.selectedItemKeys.length) {
        this.selectedItemKeys = nextKeys
      }
    },
    selectAllEligible() {
      const keys = []
      if (this.pendingReview?.items && this.pendingReview?.status !== 'STALE') {
        for (const item of this.pendingReview.items) {
          if (this.isItemEligibleForApproval(item)) {
            keys.push(item.itemKey)
          }
        }
      }
      this.selectedItemKeys = keys
    },
    deselectAll() {
      this.selectedItemKeys = []
    },
    toggleSelectAllCreate() {
      const eligibleCreateKeys = this.pendingCreateItems.filter(i => this.isItemEligibleForApproval(i)).map(i => i.itemKey)
      if (this.areAllCreateSelected) {
        this.selectedItemKeys = this.selectedItemKeys.filter(k => !eligibleCreateKeys.includes(k))
      } else {
        const set = new Set([...this.selectedItemKeys, ...eligibleCreateKeys])
        this.selectedItemKeys = Array.from(set)
      }
    },
    toggleSelectAllUpdate() {
      const eligibleUpdateKeys = this.pendingUpdateItems.filter(i => this.isItemEligibleForApproval(i)).map(i => i.itemKey)
      if (this.areAllUpdateSelected) {
        this.selectedItemKeys = this.selectedItemKeys.filter(k => !eligibleUpdateKeys.includes(k))
      } else {
        const set = new Set([...this.selectedItemKeys, ...eligibleUpdateKeys])
        this.selectedItemKeys = Array.from(set)
      }
    },
    isItemEligibleForApproval(item) {
      if (!item) return false
      if (this.pendingReview?.status === 'STALE') return false
      if (item.status !== 'PENDING') return false

      const isCodeDrift = item.category === 'CODE_DRIFT' || item.changeType === 'CODE_DRIFT' || item.changeType === 'CODE_CHANGE'
      if (isCodeDrift) {
        const mappedId = (this.codeMappings && this.codeMappings[item.itemKey]) || item.targetData?._id
        if (!mappedId || (typeof mappedId === 'string' && !mappedId.trim())) return false
        const remainingBlockers = (Array.isArray(item.blockers) ? item.blockers : []).filter(
          b => b !== 'CODE_DRIFT_UNMAPPED' && b !== 'CODE_DRIFT_REQUIRES_ADMIN_MAPPING'
        )
        if (remainingBlockers.length > 0) return false
        return true
      }

      if (Array.isArray(item.blockers) && item.blockers.length > 0) {
        if (item.hrVerification?.readyForApproval) {
          const remaining = item.blockers.filter(b => b !== 'HR_EXACT_NAME_RESOLVER_UNVERIFIED')
          if (remaining.length > 0) return false
          return true
        }
        return false
      }

      const isUpdateName = item.category === 'UPDATE_NAME' || item.changeType === 'UPDATE_NAME' || item.changeType === 'NAME_CHANGE'
      if (isUpdateName) {
        if (item.hrVerification) {
          return Boolean(item.hrVerification.readyForApproval)
        }
        return Boolean(item.eligibleForApproval)
      }
      return Boolean(item.eligibleForApproval !== false)
    },
    formatBlockerReason(item) {
      if (item?.hrVerification?.readyForApproval) {
        return this.isEn
          ? 'HR Compatibility Verified: Legacy name support plan active (Ready for confirmation)'
          : (item.hrVerification.message || 'ตรวจ HR ผ่านแล้ว: จัดเตรียมแผนรองรับชื่อเดิมแบบ Exact Match เรียบร้อย (พร้อมยืนยันบันทึก)')
      }
      if (item?.hrVerification && !item.hrVerification.readyForApproval) {
        return item.hrVerification.message || (this.isEn ? 'HR verification incomplete' : 'การตรวจ HR ยังไม่สมบูรณ์')
      }
      if (item?.status === 'ACKNOWLEDGED_KEEP_DEFAULT') {
        return this.isEn
          ? 'Admin acknowledged: Retaining current organization unit in system'
          : 'Admin รับทราบแล้ว: คงข้อมูลสังกัดเดิมไว้ในระบบ ไม่ลบสังกัด'
      }

      const map = {
        'HR_EXACT_NAME_RESOLVER_UNVERIFIED': this.isEn
          ? 'Suspended: Awaiting verified HR dataset update (HR staff resolution matches exact Thai name — alias guessing prohibited)'
          : 'ระงับการบันทึก: ยังไม่มีหลักฐานยืนยันข้อมูล HR (ระบบค้นหาบุคคลากรใช้ชื่อภาษาไทยตรงตัว — ห้ามสร้าง alias จากการเดา)',
        'MISSING_FROM_SOURCE_KEEP_DEFAULT': this.isEn
          ? 'Not found in Master Data — retaining current record by default (no data deletion, preserves ticket history)'
          : 'ไม่พบใน Master Data — ระบบคงข้อมูลเดิมไว้เป็นค่าเริ่มต้น (ไม่มีการลบข้อมูล ป้องกันผลกระทบต่อประวัติการแจ้งซ่อม)',
        'FACULTY_MAP_TARGET_CODE_DRIFT': this.isEn
          ? 'Faculty mapping code mismatch - requires manual review'
          : 'รหัสที่เปลี่ยนส่งผลต่อการจับคู่คณะของนักศึกษา (Approved Faculty Map) ต้องตรวจสอบก่อน',
        'FACULTY_MAPPING_IMPACT_BLOCKED': this.isEn
          ? 'Impacts Approved Student Faculty Mapping - code change may disrupt faculty assignment'
          : 'รหัสเดิมผูกกับ Approved Student Faculty Mapping — การเปลี่ยนรหัสอาจทำให้ระบบจับคู่สำนักวิชานักศึกษาขัดข้อง',
        'HR_RESOLVER_CODE_COLLISION': this.isEn
          ? 'Code collision with existing HR resolver'
          : 'รหัสซ้ำซ้อนกับระบบค้นหาข้อมูลบุคคลากร',
        'CODE_DRIFT_UNMAPPED': this.isEn
          ? 'Code changed - requires specifying relationship to existing unit'
          : 'รหัสเปลี่ยนจากเดิม — ต้องระบุความสัมพันธ์กับสังกัดเดิมก่อนอนุมัติ',
        'CODE_DRIFT_REQUIRES_ADMIN_MAPPING': this.isEn
          ? 'Code changed from source - requires specifying relationship to existing unit'
          : 'รหัสเปลี่ยนจากเดิม — ต้องระบุความสัมพันธ์กับสังกัดเดิมก่อนอนุมัติ',
        'MISSING_FROM_SOURCE': this.isEn
          ? 'Not found in Master Data - retaining current data by default'
          : 'ไม่พบใน Master Data — ระบบคงข้อมูลเดิมไว้เป็นค่าเริ่มต้น (ไม่มีการลบข้อมูล)',
        'NAME_COLLISION_WITH_ANOTHER_UNIT': this.isEn
          ? 'Name collision with another existing organization unit'
          : 'ชื่อซ้ำซ้อนกับสังกัดอื่นที่มีอยู่แล้วในระบบ',
        'EXISTING_RECORD_NOT_FOUND': this.isEn
          ? 'Existing organization unit record not found in system'
          : 'ไม่พบข้อมูลสังกัดเดิมในระบบ',
        'DUPLICATE_CODE_IN_SOURCE': this.isEn
          ? 'Duplicate code in Master Data'
          : 'พบรหัสซ้ำใน Master Data',
        'MALFORMED_DATA': this.isEn
          ? 'Malformed or invalid data format'
          : 'ข้อมูลผิดรูปแบบหรือไม่ครบถ้วน',
      }

      const blockerList = Array.isArray(item?.blockers) && item.blockers.length > 0
        ? item.blockers
        : (item?.blockerReason ? [item.blockerReason] : [])

      if (blockerList.length > 0) {
        const translated = blockerList.map(b => map[b] || b)
        return translated.join(' | ')
      }

      if (item?.category === 'MISSING_FROM_SOURCE' || item?.changeType === 'MISSING_IN_MASTER') {
        return this.isEn
          ? 'Not found in Master Data — retaining current record by default (no deletion)'
          : 'ไม่พบใน Master Data — ระบบคงข้อมูลเดิมไว้เป็นค่าเริ่มต้น (ไม่มีการลบข้อมูล)'
      }

      if (item?.category === 'UPDATE_NAME' || item?.changeType === 'NAME_CHANGE') {
        return this.isEn
          ? 'Name change detected — pending review'
          : 'ตรวจพบชื่อสังกัดเปลี่ยนแปลง — อยู่ระหว่างรอตรวจสอบ'
      }

      return item?.impact || (this.isEn ? 'Requires review before approval' : 'ต้องตรวจสอบข้อมูลก่อนอนุมัติ')
    },
    openCodeDriftModal(item) {
      this.activeCodeDriftItem = item
      this.showCodeDriftModal = true
    },
    closeCodeDriftModal() {
      this.showCodeDriftModal = false
      this.activeCodeDriftItem = null
    },
    saveCodeDriftMapping(mappedId) {
      if (this.activeCodeDriftItem) {
        const itemKey = this.activeCodeDriftItem.itemKey
        if (mappedId && typeof mappedId === 'string' && mappedId.trim()) {
          this.$set(this.codeMappings, itemKey, mappedId.trim())
          this.activeCodeDriftItem.eligibleForApproval = true
          if (this.isItemEligibleForApproval(this.activeCodeDriftItem)) {
            if (!this.selectedItemKeys.includes(itemKey)) {
              this.selectedItemKeys.push(itemKey)
            }
          }
        }
      }
      this.closeCodeDriftModal()
    },
    async confirmBatchApproval() {
      if (this.busy || !this.pendingReview) return
      if (this.pendingReview.status === 'STALE') {
        await Swal.fire({
          icon: 'warning',
          title: this.isEn ? 'Review is Stale' : 'แผนนี้หมดอายุแล้ว (STALE)',
          text: this.isEn
            ? 'This review is stale and cannot be approved. Please run sync now to refresh.'
            : 'รายการใน Review ฉบับนี้หมดอายุแล้ว ไม่สามารถอนุมัติได้ กรุณากดซิงค์ข้อมูลใหม่',
        })
        return
      }
      if (this.selectedTotalCount === 0) return
      const eligibleSelectedItems = (this.pendingReview?.items || []).filter(
        i => this.selectedItemKeys.includes(i.itemKey) && this.isItemEligibleForApproval(i)
      )
      const eligibleKeys = eligibleSelectedItems.map(i => i.itemKey)
      if (eligibleKeys.length === 0) return

      const createCount = this.selectedCreateCount
      const updateCount = this.selectedUpdateCount

      const confirmation = await Swal.fire({
        icon: 'question',
        title: this.$t('requesterOrgSync.batchConfirmTitle'),
        text: this.$t('requesterOrgSync.batchConfirmDesc', { createCount, updateCount }),
        showCancelButton: true,
        confirmButtonText: this.isEn ? 'Confirm and Save' : 'ยืนยันและบันทึกข้อมูล',
        cancelButtonText: this.$t('common.cancel'),
        confirmButtonColor: '#2563eb',
      })

      if (!confirmation.isConfirmed) return

      this.approvingReview = true
      const requestId = ++this.latestRequestId
      try {
        const payload = {
          reviewId: this.pendingReview.reviewId,
          itemKeys: eligibleKeys,
          codeMappings: this.codeMappings,
        }
        const response = await Service.requesterOrganizationSync('approve', payload)
        if (requestId !== this.latestRequestId) return

        this.selectedItemKeys = this.selectedItemKeys.filter(k => !eligibleKeys.includes(k))
        if (requestId !== this.latestRequestId) return

        const resData = response?.data?.data
        const totalSucceeded = resData?.totalSucceeded || 0
        const remaining = resData?.remainingPendingCount || 0

        if (resData?.statusUpdateWarning) {
          await Swal.fire({
            icon: 'warning',
            title: this.$t('requesterOrgSync.completed'),
            text: (this.isEn ? `Applied ${totalSucceeded} item(s) successfully, but status refresh had a warning.` : `บันทึกข้อมูลสำเร็จ ${totalSucceeded} รายการ แต่อัปเดตสถานะมีคำเตือน`),
          })
        } else if (resData?.status === 'PARTIAL') {
          await Swal.fire({
            icon: 'info',
            title: this.isEn ? 'Partially Completed' : 'บันทึกสำเร็จบางส่วน',
            text: this.isEn
              ? `Successfully applied ${totalSucceeded} item(s). ${remaining} item(s) remain pending.`
              : `บันทึกสำเร็จ ${totalSucceeded} รายการ ยังคงมีรายการค้างตรวจสอบอีก ${remaining} รายการ`,
          })
        } else {
          await Swal.fire({
            icon: 'success',
            title: this.$t('requesterOrgSync.completed'),
            text: this.isEn
              ? `Successfully applied ${totalSucceeded} item(s) (Created: ${resData?.succeededCreateCount || 0}, Updated: ${resData?.succeededUpdateCount || 0})`
              : `บันทึกข้อมูลเรียบร้อย ${totalSucceeded} รายการ (เพิ่มใหม่: ${resData?.succeededCreateCount || 0}, แก้ไข: ${resData?.succeededUpdateCount || 0})`,
          })
        }

        await this.loadSettings()
        await this.loadPendingReview()
        await this.loadHistory(this.historyPagination.page || 1)
      } catch (error) {
        if (requestId !== this.latestRequestId) return
        const errCode = this.errorCode(error)
        if (errCode === 'PLAN_STALE') {
          await Swal.fire({
            icon: 'warning',
            title: this.isEn ? 'Data Changed' : 'ข้อมูลต้นทางเปลี่ยนแปลง',
            text: this.isEn
              ? 'Master Data changed while pending. Review refreshed.'
              : 'ข้อมูล Master Data มีการเปลี่ยนแปลงระหว่างรออนุมัติ ระบบจะโหลดรายการล่าสุดให้ตรวจสอบใหม่',
          })
          await this.loadPendingReview()
        } else {
          await Swal.fire({
            icon: 'error',
            title: this.$t('requesterOrgSync.applyFailed'),
            text: `${this.$t('requesterOrgSync.errorCode')}: ${errCode}`,
          })
        }
      } finally {
        this.approvingReview = false
      }
    },
    async confirmPostpone() {
      if (this.busy || !this.pendingReview) return
      if (this.pendingReview.status === 'STALE') {
        await Swal.fire({
          icon: 'warning',
          title: this.isEn ? 'Review is Stale' : 'แผนนี้หมดอายุแล้ว (STALE)',
          text: this.isEn
            ? 'This review is stale and cannot be modified.'
            : 'รายการใน Review ฉบับนี้หมดอายุแล้ว ไม่สามารถดำเนินการได้',
        })
        return
      }
      const confirmation = await Swal.fire({
        icon: 'info',
        title: this.$t('requesterOrgSync.postponeBtn'),
        text: this.isEn
          ? 'Postpone reviewing these items. The system will continue to reflect drift status until confirmed.'
          : 'เลื่อนการตัดสินใจสำหรับรายการเหล่านี้ ข้อมูลเดิมจะคงไว้ และสถานะจะยังสะท้อนความต่างจาก Master Data จนกว่าจะยืนยัน',
        showCancelButton: true,
        confirmButtonText: this.$t('requesterOrgSync.postponeBtn'),
        cancelButtonText: this.$t('common.cancel'),
      })

      if (!confirmation.isConfirmed) return

      this.postponingReview = true
      const requestId = ++this.latestRequestId
      try {
        const payload = {
          reviewId: this.pendingReview.reviewId,
          itemKeys: this.selectedItemKeys.length > 0 ? this.selectedItemKeys : [],
        }
        await Service.requesterOrganizationSync('postpone', payload)
        if (requestId !== this.latestRequestId) return

        await Swal.fire({
          icon: 'success',
          title: this.$t('requesterOrgSync.completed'),
          text: this.isEn ? 'Decision postponed' : 'เลื่อนการตัดสินใจเรียบร้อย',
        })
        await this.loadPendingReview()
        await this.loadHistory(this.historyPagination.page || 1)
      } catch (error) {
        if (requestId !== this.latestRequestId) return
        await Swal.fire({
          icon: 'error',
          title: this.$t('requesterOrgSync.applyFailed'),
          text: `${this.$t('requesterOrgSync.errorCode')}: ${this.errorCode(error)}`,
        })
      } finally {
        this.postponingReview = false
      }
    },
    async verifySingleHr(item) {
      if (!this.pendingReview || this.verifyingItemKey) return
      this.verifyingItemKey = item.itemKey
      try {
        const payload = {
          reviewId: this.pendingReview.reviewId,
          itemKeys: [item.itemKey],
        }
        const res = await Service.requesterOrganizationSync('verifyHr', payload)
        const itemResult = res?.data?.data?.items?.[0]
        if (itemResult?.readyForApproval) {
          await Swal.fire({
            icon: 'success',
            title: this.isEn ? 'HR Compatibility Verified' : 'ตรวจความสอดคล้องกับ HR สำเร็จ',
            text: this.isEn
              ? 'Exact-match legacy name support prepared. Item is now ready for approval.'
              : (itemResult.message || 'จัดเตรียมแผนรองรับชื่อเดิมแบบ Exact Match เรียบร้อย พร้อมอนุมัติเปลี่ยนชื่อ'),
          })
        } else {
          await Swal.fire({
            icon: 'warning',
            title: this.isEn ? 'HR Verification Conditions Remain' : 'ยังมีเงื่อนไขการตรวจ HR',
            text: itemResult?.message || (this.isEn ? 'Could not fully verify HR compatibility.' : 'ไม่สามารถยืนยันความสอดคล้องกับ HR ได้'),
          })
        }
        await this.loadPendingReview(this.pendingReview.reviewId)
      } catch (err) {
        await Swal.fire({
          icon: 'error',
          title: this.isEn ? 'Verification Failed' : 'การตรวจสอบล้มเหลว',
          text: `${this.$t('requesterOrgSync.errorCode')}: ${this.errorCode(err)}`,
        })
      } finally {
        this.verifyingItemKey = null
      }
    },
    async verifyAllHrCompatibility() {
      if (!this.pendingReview || this.verifyingHr) return
      this.verifyingHr = true
      try {
        const payload = {
          reviewId: this.pendingReview.reviewId,
        }
        const res = await Service.requesterOrganizationSync('verifyHr', payload)
        const data = res?.data?.data
        const readyCount = data?.readyCount || 0
        const total = data?.verifiedCount || 0
        await Swal.fire({
          icon: readyCount > 0 ? 'success' : 'info',
          title: this.isEn ? 'HR Compatibility Verification Complete' : 'ตรวจสอบความสอดคล้องกับ HR ครบถ้วน',
          text: this.isEn
            ? `Verified ${total} item(s). ${readyCount} item(s) are ready for approval with legacy name support.`
            : `ตรวจสอบแล้ว ${total} รายการ: พร้อมอนุมัติและรองรับชื่อเดิม ${readyCount} รายการ`,
        })
        await this.loadPendingReview(this.pendingReview.reviewId)
      } catch (err) {
        await Swal.fire({
          icon: 'error',
          title: this.isEn ? 'Verification Failed' : 'การตรวจสอบล้มเหลว',
          text: `${this.$t('requesterOrgSync.errorCode')}: ${this.errorCode(err)}`,
        })
      } finally {
        this.verifyingHr = false
      }
    },
    async keepDefaultItem(item) {
      if (!this.pendingReview || this.acknowledgingItemKey) return
      const confirm = await Swal.fire({
        title: this.isEn ? 'Keep Existing Organization?' : 'คงข้อมูลสังกัดเดิมไว้?',
        html: `<div class="text-left small"><p>${this.isEn ? 'Keep this unit active in the system without deleting or changing OrganizationUnit data:' : 'คงสังกัดนี้ไว้ในระบบโดยไม่มีการลบหรือแก้ไขข้อมูล OrganizationUnit:'}</p><code>${this.getItemCode(item)} - ${this.getExistingName(item)}</code><p class="text-muted mt-2">${this.isEn ? 'This acknowledges the item as distinct from Master Data.' : 'ระบบจะบันทึกสถานะว่า Admin รับทราบความต่างจาก Master Data แล้ว'}</p></div>`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: this.isEn ? 'Confirm Keep' : 'ยืนยันคงข้อมูลเดิม',
        cancelButtonText: this.$t('common.cancel'),
      })
      if (!confirm.isConfirmed) return

      this.acknowledgingItemKey = item.itemKey
      try {
        const payload = {
          reviewId: this.pendingReview.reviewId,
          itemKeys: [item.itemKey],
        }
        await Service.requesterOrganizationSync('keepDefault', payload)
        await Swal.fire({
          icon: 'success',
          title: this.isEn ? 'Acknowledged' : 'บันทึกการรับทราบเรียบร้อย',
          text: this.isEn
            ? 'Retained existing organization unit in system. No data was deleted.'
            : 'บันทึกรับทราบและคงข้อมูลสังกัดเดิมไว้เรียบร้อย ไม่มีการลบข้อมูล',
        })
        await this.loadPendingReview(this.pendingReview.reviewId)
        await this.loadHistory(this.historyPagination.page || 1)
      } catch (err) {
        await Swal.fire({
          icon: 'error',
          title: this.isEn ? 'Action Failed' : 'ดำเนินการไม่สำเร็จ',
          text: `${this.$t('requesterOrgSync.errorCode')}: ${this.errorCode(err)}`,
        })
      } finally {
        this.acknowledgingItemKey = null
      }
    },
    async postponeSingleItem(item) {
      if (!this.pendingReview || this.postponingItemKey) return
      this.postponingItemKey = item.itemKey
      try {
        const payload = {
          reviewId: this.pendingReview.reviewId,
          itemKeys: [item.itemKey],
        }
        await Service.requesterOrganizationSync('postpone', payload)
        await Swal.fire({
          icon: 'info',
          title: this.isEn ? 'Postponed' : 'เลื่อนการตัดสินใจแล้ว',
          text: this.isEn
            ? 'Item has been marked as postponed. OrganizationUnit was not modified.'
            : 'เลื่อนการตัดสินใจรายการนี้แล้ว ไม่มีการแก้ไขข้อมูลสังกัดในระบบ',
        })
        await this.loadPendingReview(this.pendingReview.reviewId)
        await this.loadHistory(this.historyPagination.page || 1)
      } catch (err) {
        await Swal.fire({
          icon: 'error',
          title: this.isEn ? 'Postpone Failed' : 'เลื่อนการตัดสินใจไม่สำเร็จ',
          text: `${this.$t('requesterOrgSync.errorCode')}: ${this.errorCode(err)}`,
        })
      } finally {
        this.postponingItemKey = null
      }
    },
    async resumeSingleItem(item) {
      if (!this.pendingReview || this.resumingItemKey) return
      this.resumingItemKey = item.itemKey
      try {
        const payload = {
          reviewId: this.pendingReview.reviewId,
          itemKeys: [item.itemKey],
        }
        await Service.requesterOrganizationSync('resume', payload)
        await Swal.fire({
          icon: 'success',
          title: this.isEn ? 'Resumed for Approval' : 'นำกลับมารออนุมัติแล้ว',
          text: this.isEn
            ? 'Item is now pending review and ready for approval. OrganizationUnit was not modified.'
            : 'นำรายการกลับมารออนุมัติแล้ว ไม่มีการแก้ไขข้อมูลสังกัดในระบบ',
        })
        await this.loadPendingReview(this.pendingReview.reviewId)
        await this.loadHistory(this.historyPagination.page || 1)
      } catch (err) {
        await Swal.fire({
          icon: 'error',
          title: this.isEn ? 'Resume Failed' : 'นำกลับมารออนุมัติไม่สำเร็จ',
          text: `${this.$t('requesterOrgSync.errorCode')}: ${this.errorCode(err)}`,
        })
      } finally {
        this.resumingItemKey = null
      }
    },
    switchTab(tab) {
      this.activeTab = tab
      if (tab === 'UPDATE_ORG') {
        this.loadRecentUpdates()
      } else if (tab === 'CHANGE_HISTORY' && (!this.historyItems || this.historyItems.length === 0)) {
        this.loadHistory(1)
      }
    },
    async loadHistory(page = 1) {
      this.historyLoading = true
      try {
        const params = {
          page,
          limit: this.historyPagination?.limit || 10,
        }
        if (this.historySearch && this.historySearch.trim()) {
          params.search = this.historySearch.trim()
        }
        if (this.historyActionType) {
          params.actionType = this.historyActionType
        }
        if (this.historyStartDate) {
          params.startDate = this.historyStartDate
        }
        if (this.historyEndDate) {
          params.endDate = this.historyEndDate
        }
        const response = await Service.requesterOrganizationSync('getHistory', null, { params })
        const data = response?.data?.data
        if (data) {
          this.historyItems = data.items || []
          const p = data.pagination || {}
          this.historyPagination = {
            page: p.page || page || 1,
            limit: p.limit || p.pageSize || 10,
            pageSize: p.pageSize || p.limit || 10,
            total: typeof p.total === 'number' ? p.total : (typeof p.totalItems === 'number' ? p.totalItems : 0),
            totalItems: typeof p.totalItems === 'number' ? p.totalItems : (typeof p.total === 'number' ? p.total : 0),
            totalPages: p.totalPages || 1,
            hasNextPage: !!p.hasNextPage,
            hasPrevPage: !!p.hasPrevPage,
          }
        }
      } catch (err) {
        console.error('Failed to load requester org history:', err)
      } finally {
        this.historyLoading = false
      }
    },
    resetHistoryFilters() {
      this.historySearch = ''
      this.historyActionType = ''
      this.historyStartDate = ''
      this.historyEndDate = ''
      this.loadHistory(1)
    },
    getBeforeValue(item) {
      if (!item) return '-'
      return item.beforeValue || item.previousValue || '-'
    },
    getAfterValue(item) {
      if (!item) return '-'
      return item.afterValue || item.newValue || '-'
    },
    getActionBadgeClass(actionType) {
      if (!actionType) return 'badge-secondary'
      if (actionType === 'ORGANIZATION_UNIT_CREATE' || actionType === 'CREATE') return 'badge-info'
      if (actionType === 'ORGANIZATION_UNIT_UPDATE_NAME' || actionType === 'UPDATE_NAME') return 'badge-primary'
      if (actionType === 'ORGANIZATION_UNIT_UPDATE_CODE' || actionType === 'UPDATE_CODE' || actionType === 'CODE_DRIFT') return 'badge-warning text-dark'
      if (actionType === 'ORGANIZATION_UNIT_KEEP_DEFAULT' || actionType === 'KEEP_DEFAULT') return 'badge-secondary'
      if (actionType === 'ORGANIZATION_UNIT_POSTPONE' || actionType === 'POSTPONE' || actionType === 'POSTPONED') return 'badge-light border text-muted'
      if (actionType === 'ORGANIZATION_UNIT_RESUME' || actionType === 'RESUME') return 'badge-success'
      return 'badge-secondary'
    },
    getActionTypeLabel(actionType) {
      if (!actionType) return '-'
      const map = {
        'ORGANIZATION_UNIT_CREATE': this.$t('requesterOrgSync.historyActionCreate'),
        'CREATE': this.$t('requesterOrgSync.historyActionCreate'),
        'ORGANIZATION_UNIT_UPDATE_NAME': this.$t('requesterOrgSync.historyActionUpdateName'),
        'UPDATE_NAME': this.$t('requesterOrgSync.historyActionUpdateName'),
        'ORGANIZATION_UNIT_UPDATE_CODE': this.$t('requesterOrgSync.historyActionUpdateCode'),
        'UPDATE_CODE': this.$t('requesterOrgSync.historyActionUpdateCode'),
        'CODE_DRIFT': this.$t('requesterOrgSync.historyActionUpdateCode'),
        'ORGANIZATION_UNIT_KEEP_DEFAULT': this.$t('requesterOrgSync.historyActionKeepDefault'),
        'KEEP_DEFAULT': this.$t('requesterOrgSync.historyActionKeepDefault'),
        'ORGANIZATION_UNIT_POSTPONE': this.$t('requesterOrgSync.historyActionPostpone'),
        'POSTPONE': this.$t('requesterOrgSync.historyActionPostpone'),
        'ORGANIZATION_UNIT_RESUME': this.$t('requesterOrgSync.historyActionResume'),
        'RESUME': this.$t('requesterOrgSync.historyActionResume'),
      }
      return map[actionType] || actionType
    },
    getProvenanceLabel(provenance) {
      if (provenance === 'E2E_VERIFICATION') return this.isEn ? 'E2E Verification' : 'การทดสอบ E2E'
      if (provenance === 'RECOVERY_EVENT') return this.isEn ? 'System Recovery' : 'การกู้คืนระบบ'
      if (provenance === 'ADMIN_ACTION') return this.isEn ? 'Admin Action' : 'การกระทำโดย Admin'
      return provenance || '-'
    },
  },
}
</script>

<style scoped>
.sync-page-container {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 4px 32px 4px;
  box-sizing: border-box;
}

.sync-header-text {
  min-width: 0;
  flex: 1 1 auto;
}

/* Page Header */
.sync-header-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent !important;
  color: #ffffff;
  flex-shrink: 0;
}

/* Hero Banner matching Admin Building Management style */
.sync-hero-banner {
  background: #1e40af !important;
  color: #ffffff !important;
  border-radius: 12px !important;
  padding: 20px 28px !important;
  min-height: 88px;
  height: auto !important;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
}

.sync-hero-banner .card-title {
  color: #ffffff !important;
  font-size: clamp(1.2rem, 2vw, 1.55rem);
  font-weight: 800;
  letter-spacing: -0.01em;
}

.sync-hero-banner .sync-header-sub {
  color: rgba(255, 255, 255, 0.88);
  font-size: 0.875rem;
  line-height: 1.4;
  word-break: break-word;
}

.sync-hero-banner .sync-header-icon svg {
  color: #ffffff;
  stroke: #ffffff;
}

@media (max-width: 576px) {
  .sync-hero-banner {
    padding: 16px 18px !important;
  }
}

/* Prominent Status Banner */
.sync-prominent-status {
  border-left: 6px solid transparent;
  transition: all 0.2s ease;
}

.sync-prominent-status--green {
  background-color: #f0fdf4;
  border-color: #22c55e;
}

.sync-prominent-status--red {
  background-color: #fef2f2;
  border-color: #ef4444;
}

.sync-prominent-status--neutral {
  background-color: #f8fafc;
  border-color: #94a3b8;
}

.sync-banner-icon-box {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
}

/* Card Styling */
.sync-card {
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02);
  transition: all 0.2s ease;
  overflow: hidden;
}

.sync-card-header {
  border-bottom: 1px solid #f1f5f9;
  padding: 16px 20px;
}

.sync-card-title {
  font-size: 1rem;
  color: #1e293b;
  letter-spacing: -0.01em;
}

.sync-card-body {
  padding: 20px;
}

/* Switches */
.sync-switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  margin-bottom: 0;
  vertical-align: middle;
}

.sync-switch-input {
  opacity: 0;
  width: 0;
  height: 0;
}

.sync-switch-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #cbd5e1;
  transition: 0.2s;
  border-radius: 24px;
}

.sync-switch-slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.2s;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}

.sync-switch-input:checked + .sync-switch-slider {
  background-color: #2563eb;
}

.sync-switch-input:checked + .sync-switch-slider:before {
  transform: translateX(20px);
}

.sync-switch--impact .sync-switch-input:checked + .sync-switch-slider {
  background-color: #0284c7;
}

/* Buttons */
.sync-btn {
  font-weight: 600;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease-in-out;
}

.sync-btn--primary {
  background-color: #2563eb;
  border-color: #2563eb;
  color: #ffffff;
}

.sync-btn--primary:hover:not(:disabled) {
  background-color: #1d4ed8;
  border-color: #1d4ed8;
}

.sync-btn--apply {
  background-color: #16a34a;
  border-color: #16a34a;
  color: #ffffff;
}

.sync-btn--apply:hover:not(:disabled) {
  background-color: #15803d;
  border-color: #15803d;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
